import React, { useState, useEffect, useRef, useCallback } from 'react';
import QuillEditor from '../components/QuillEditor';
import CityAutocomplete from '../components/CityAutocomplete';
import { formatCityDisplay, generateClinicSlug, extractDistrictFromCity } from '../utils/helpers';

const AdminPanel = ({ adminView, setAdminView, adminToken, backendUrl, handleAdminLogout }) => {
  // AdminMessages component - moved inside AdminPanel to access makeAuthenticatedRequest
  const AdminMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, archived: 0 });

    // Fetch messages
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const statusParam = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
        const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/messages${statusParam}`);
        if (response.ok) {
          const data = await response.json();
          setMessages(data);
        } else {
          console.error('Hiba az üzenetek lekérésekor, status:', response.status);
        }
      } catch (error) {
        console.error('Hiba az üzenetek betöltésekor:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch stats
    const fetchStats = async () => {
      try {
        const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/messages/stats/count`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Hiba a statisztikák betöltésekor:', error);
      }
    };

    useEffect(() => {
      fetchMessages();
      fetchStats();
    }, [filterStatus]);

    // View message details
    const viewMessage = async (messageId) => {
      try {
        const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/messages/${messageId}`);
        if (response.ok) {
          const data = await response.json();
          setSelectedMessage(data);
          // Refresh messages and stats after viewing (to update read status)
          fetchMessages();
          fetchStats();
        }
      } catch (error) {
        console.error('Hiba az üzenet betöltésekor:', error);
      }
    };

    // Update message status
    const updateStatus = async (messageId, status) => {
      try {
        const response = await makeAuthenticatedRequest(
          `${backendUrl}/api/admin/messages/${messageId}/status?status=${status}`,
          { method: 'PUT' }
        );
        if (response.ok) {
          fetchMessages();
          fetchStats();
          if (selectedMessage && selectedMessage.id === messageId) {
            setSelectedMessage(prev => ({ ...prev, status }));
          }
        }
      } catch (error) {
        console.error('Hiba a státusz frissítésekor:', error);
      }
    };

    // Delete message
    const deleteMessage = async (messageId) => {
      if (!confirm('Biztosan törölni szeretnéd ezt az üzenetet?')) return;
      
      try {
        const response = await makeAuthenticatedRequest(
          `${backendUrl}/api/admin/messages/${messageId}`,
          { method: 'DELETE' }
        );
        if (response.ok) {
          fetchMessages();
          fetchStats();
          if (selectedMessage && selectedMessage.id === messageId) {
            setSelectedMessage(null);
          }
          alert('Üzenet törölve!');
        }
      } catch (error) {
        console.error('Hiba az üzenet törlésekor:', error);
        alert('Hiba történt az üzenet törlésekor!');
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Üzenetek</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Összes</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <div className="text-sm text-blue-600">Olvasatlan</div>
            <div className="text-2xl font-bold text-blue-700">{stats.unread}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow">
            <div className="text-sm text-green-600">Olvasott</div>
            <div className="text-2xl font-bold text-green-700">{stats.read}</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Archivált</div>
            <div className="text-2xl font-bold text-gray-700">{stats.archived}</div>
          </div>
        </div>

        {/* Filter buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-md ${filterStatus === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Összes
          </button>
          <button
            onClick={() => setFilterStatus('new')}
            className={`px-4 py-2 rounded-md ${filterStatus === 'new' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Olvasatlan
          </button>
          <button
            onClick={() => setFilterStatus('read')}
            className={`px-4 py-2 rounded-md ${filterStatus === 'read' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Olvasott
          </button>
          <button
            onClick={() => setFilterStatus('archived')}
            className={`px-4 py-2 rounded-md ${filterStatus === 'archived' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Archivált
          </button>
        </div>

        {/* Messages list and detail view */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages list */}
          <div className="bg-white rounded-lg shadow-md p-6 overflow-y-auto" style={{ maxHeight: '70vh' }}>
            <h3 className="text-xl font-semibold mb-4">Bejövő üzenetek</h3>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Betöltés...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nincs üzenet</div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    onClick={() => viewMessage(message.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedMessage?.id === message.id
                        ? 'border-blue-500 bg-blue-50'
                        : message.status === 'new'
                        ? 'border-gray-300 bg-blue-50 hover:bg-blue-100'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-gray-800">{message.name}</div>
                      {message.status === 'new' && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">Új</span>
                      )}
                      {message.status === 'archived' && (
                        <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">Archivált</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{message.clinic_name}</div>
                    <div className="text-xs text-gray-500 mt-2">{formatDate(message.submitted_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message detail */}
          <div className="bg-white rounded-lg shadow-md p-6 overflow-y-auto" style={{ maxHeight: '70vh' }}>
            {selectedMessage ? (
              <div>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Üzenet részletei</h3>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Név:</label>
                    <div className="text-gray-800">{selectedMessage.name}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email:</label>
                    <div className="text-gray-800">
                      <a href={`mailto:${selectedMessage.email}`} className="text-blue-600 hover:underline">
                        {selectedMessage.email}
                      </a>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Telefon:</label>
                    <div className="text-gray-800">
                      <a href={`tel:${selectedMessage.phone}`} className="text-blue-600 hover:underline">
                        {selectedMessage.phone}
                      </a>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Klinika neve:</label>
                    <div className="text-gray-800">{selectedMessage.clinic_name}</div>
                  </div>
                  {selectedMessage.website && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Weboldal:</label>
                      <div className="text-gray-800">
                        <a href={selectedMessage.website.startsWith('http') ? selectedMessage.website : `https://${selectedMessage.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {selectedMessage.website}
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedMessage.services && selectedMessage.services.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Érdeklődési terület:</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedMessage.services.map((service, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMessage.message && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Üzenet:</label>
                      <div className="text-gray-800 whitespace-pre-wrap">{selectedMessage.message}</div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-600">Beküldve:</label>
                    <div className="text-gray-800">{formatDate(selectedMessage.submitted_at)}</div>
                  </div>
                  {selectedMessage.read_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Olvasva:</label>
                      <div className="text-gray-800">{formatDate(selectedMessage.read_at)}</div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {selectedMessage.status === 'new' && (
                    <button
                      onClick={() => updateStatus(selectedMessage.id, 'read')}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Olvasottnak jelöl
                    </button>
                  )}
                  {selectedMessage.status === 'read' && (
                    <>
                      <button
                        onClick={() => updateStatus(selectedMessage.id, 'new')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Olvasatlannak jelöl
                      </button>
                      <button
                        onClick={() => updateStatus(selectedMessage.id, 'archived')}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                      >
                        Archiválás
                      </button>
                    </>
                  )}
                  {selectedMessage.status === 'archived' && (
                    <button
                      onClick={() => updateStatus(selectedMessage.id, 'read')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Visszaállítás
                    </button>
                  )}
                  <button
                    onClick={() => deleteMessage(selectedMessage.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Törlés
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p>Válassz egy üzenetet a részletek megtekintéséhez</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleClinicOgImageUpload = async (file) => {
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete túl nagy! Maximum 5MB engedélyezett.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Csak JPEG, PNG, GIF és WebP képformátumok engedélyezettek!');
      return;
    }

    try {
      setOgImageUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      // Ideiglenes clinic ID (új klinikánál)
      const tempClinicId = 'temp_' + Date.now();
      
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/uploads/clinic/${tempClinicId}/og-image`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setNewClinic(prev => ({
          ...prev,
          og_image: data.url
        }));
        alert('OpenGraph kép sikeresen feltöltve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba a kép feltöltése során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba az OG kép feltöltése során:', error);
      alert('Hiba történt a kép feltöltése során!');
    } finally {
      setOgImageUploading(false);
    }
  };

  const handleEditClinicOgImageUpload = async (file) => {
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete túl nagy! Maximum 5MB engedélyezett.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Csak JPEG, PNG, GIF és WebP képformátumok engedélyezettek!');
      return;
    }

    try {
      setOgImageUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/uploads/clinic/${editingClinic}/og-image`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setEditClinic(prev => ({
          ...prev,
          og_image: data.url
        }));
        alert('OpenGraph kép sikeresen feltöltve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba a kép feltöltése során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba az OG kép feltöltése során:', error);
      alert('Hiba történt a kép feltöltése során!');
    } finally {
      setOgImageUploading(false);
    }
  };

  // Debug useEffect to track adminView changes
  useEffect(() => {
    console.log('🎯 AdminView changed to:', adminView);
  }, [adminView]);
  
  const [allClinics, setAllClinics] = useState([]);
  const [allBlogPosts, setAllBlogPosts] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [services, setServices] = useState([]);

  // Load services list
  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/services`);
        const data = await response.json();
        setServices(data.services || []);
      } catch (error) {
        console.error('Hiba a szolgáltatások betöltése során:', error);
      }
    };
    loadServices();
  }, [backendUrl]);
  // Settings state
  const [settings, setSettings] = useState({
    ga4_tracking_id: '',
    default_og_image: '',
    visitor_stats_image: '',
    monthly_visitors: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [ogImageUploading, setOgImageUploading] = useState(false);
  const [visitorStatsImageUploading, setVisitorStatsImageUploading] = useState(false);
  // Legal Pages state
  const [legalPages, setLegalPages] = useState({
    privacy: { title: '', content: '' },
    terms: { title: '', content: '' }
  });
  const [legalPagesLoading, setLegalPagesLoading] = useState(false);
  const [legalPagesSaved, setLegalPagesSaved] = useState(false);
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({
    rating: 5,
    name: '',
    clinic_name: '',
    content: ''
  });
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialImageUploading, setTestimonialImageUploading] = useState(false);
  // Pagination for clinics
  const [clinicsPage, setClinicsPage] = useState(1);
  const clinicsPerPage = 30;
const [partnerFormData, setPartnerFormData] = useState({
  name: '',
  website: '',
  facebook_url: '',
  instagram_url: '', 
  linkedin_url: '',
  order: 1
});
  const [expiringClinics, setExpiringClinics] = useState([]);
  const [clinicSearch, setClinicSearch] = useState('');
  const [clinicSortBy, setClinicSortBy] = useState('name');
  const [clinicSortOrder, setClinicSortOrder] = useState('asc');
  // Bulk operations state
  const [selectedClinicIds, setSelectedClinicIds] = useState([]);
  const [bulkOperation, setBulkOperation] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [newClinic, setNewClinic] = useState({
    name: '', city: '', district: '', phone: '', email: '', address: '', website: '',
    facebook_url: '', instagram_url: '', linkedin_url: '', youtube_url: '', intro_video_url: '', foglaljorvost_link: '', google_business_link: '',
    services: [], opening_hours: '', description: '', subscription_start: '', subscription_end: '',
    has_subpage: false, subpage_url: '',
    // SEO mezők
    meta_title: '', meta_description: '', og_image: ''
  });
  const [editingClinic, setEditingClinic] = useState(null);
  const [editClinic, setEditClinic] = useState({
    name: '', city: '', district: '', phone: '', email: '', address: '', website: '',
    facebook_url: '', instagram_url: '', linkedin_url: '', youtube_url: '', intro_video_url: '', foglaljorvost_link: '', google_business_link: '',
    services: [], opening_hours: '', description: '', subscription_start: '', subscription_end: '',
    expiry_date: '', has_subpage: false, subpage_url: '',
    // SEO mezők
    meta_title: '', meta_description: '', og_image: ''
  });
  const [newBlogPost, setNewBlogPost] = useState({
    title: '', content: '', excerpt: '', author: '', status: 'draft',
    categories: [], tags: [], meta_title: '', meta_description: '', published_at: ''
  });
  const [editingPost, setEditingPost] = useState(null);
  const [editBlogPost, setEditBlogPost] = useState({
    title: '', content: '', excerpt: '', author: '', status: 'draft',
    categories: [], tags: [], meta_title: '', meta_description: '', published_at: ''
  });
  const [blogCategories, setBlogCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [featuredImageFile, setFeaturedImageFile] = useState(null);
  const [editFeaturedImageFile, setEditFeaturedImageFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  // Edit clinic image states
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]); // Track images to delete
  const [deleteCurrentLogo, setDeleteCurrentLogo] = useState(false); // Track if logo should be deleted
  
  // Widget states
  const [widgetSize, setWidgetSize] = useState('medium');
  const [widgetStyle, setWidgetStyle] = useState('light');
  const [widgetEmbedCode, setWidgetEmbedCode] = useState('');
  const [widgetManualCode, setWidgetManualCode] = useState('');
  const [showWidgetPreview, setShowWidgetPreview] = useState(false);

  // Widget functions
  const generateWidgetCode = async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/widget/embed-code?size=${widgetSize}&style=${widgetStyle}&domain=${encodeURIComponent('https://fogportal.hu')}`, 
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setWidgetEmbedCode(data.embed_code);
        setWidgetManualCode(data.manual_code);
        setShowWidgetPreview(true);
      } else {
        console.error('Hiba a widget kód generálása során');
      }
    } catch (error) {
      console.error('Hiba a widget kód generálása során:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Kód vágólapra másolva!');
    } catch (err) {
      console.error('Vágólapra másolás sikertelen:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Kód vágólapra másolva!');
    }
  };

  useEffect(() => {
    // Guard: Do not trigger fetch operations when on import view to prevent state reset
    if (adminView === 'import') {
      return;
    }
    
    if (adminView === 'dashboard' || adminView === 'clinics') {
      fetchAllClinics();
      fetchExpiringClinics();
    }
    if (adminView === 'dashboard' || adminView === 'blog') {
      fetchAllBlogPosts();
      fetchBlogCategories();
    }
    if (adminView === 'dashboard' || adminView === 'partners') {
      fetchAllPartners();
    }
    if (adminView === 'settings') {
      fetchSettings();
      loadLegalPages();
      fetchTestimonials();
    }
  }, [adminView]);

  // Separate effect for import view to load clinic fields - DISABLED to prevent infinite loop
  // useEffect(() => {
  //   if (adminView === 'import') {
  //     fetchClinicFields();
  //   }
  // }, [adminView, fetchClinicFields]);

  // Re-fetch clinics when search or sort parameters change
  useEffect(() => {
    if (adminView === 'clinics') {
      fetchAllClinics();
    }
  }, [clinicSearch, clinicSortBy, clinicSortOrder, adminView]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${adminToken}`
    };
    
    return fetch(url, { ...options, headers });
  };

  // Data Migration Component
  const DataMigrationView = () => {
    const [collectionsInfo, setCollectionsInfo] = useState({});
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [exportData, setExportData] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [importResults, setImportResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importSettings, setImportSettings] = useState({
      overwriteExisting: false,
      validateOnly: false
    });

    // Load collections info on mount
    useEffect(() => {
      fetchCollectionsInfo();
    }, []);

    const fetchCollectionsInfo = async () => {
      try {
        setLoading(true);
        const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/collections-info`);
        if (response.ok) {
          const data = await response.json();
          setCollectionsInfo(data);
          console.log('Collections info loaded:', data);
        } else {
          alert('Hiba a gyűjtemény információk betöltésekor');
        }
      } catch (error) {
        console.error('Hiba:', error);
        alert('Hiba történt a gyűjtemények betöltése során');
      } finally {
        setLoading(false);
      }
    };

    const handleExportData = async (forceFullExport = false) => {
      try {
        setLoading(true);
        
        // If force full export or no collections selected, export everything
        const collectionsToExport = forceFullExport ? null : (selectedCollections.length > 0 ? selectedCollections : null);
        
        const exportRequest = {
          collections: collectionsToExport,
          include_metadata: true
        };
        
        console.log('🔍 Export request:', exportRequest);

        const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/export-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(exportRequest)
        });

        if (response.ok) {
          const data = await response.json();
          setExportData(data);
          
          // Create and download the file
          const blob = new Blob([JSON.stringify(data.export_data, null, 2)], {
            type: 'application/json'
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          alert('Adatok sikeresen exportálva és letöltve!');
        } else {
          const errorData = await response.json();
          alert(`Hiba az export során: ${errorData.detail}`);
        }
      } catch (error) {
        console.error('Export hiba:', error);
        alert('Hiba történt az export során!');
      } finally {
        setLoading(false);
      }
    };

    const handleImportData = async () => {
      if (!importFile) {
        alert('Kérjük válasszon egy import fájlt!');
        return;
      }

      try {
        setLoading(true);
        const fileContent = await importFile.text();
        const importData = JSON.parse(fileContent);

        const importRequest = {
          data: importData,
          overwrite_existing: importSettings.overwriteExisting,
          validate_only: importSettings.validateOnly
        };

        const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/import-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(importRequest)
        });

        if (response.ok) {
          const results = await response.json();
          setImportResults(results);
          
          if (importSettings.validateOnly) {
            alert('Validáció kész! Ellenőrizze az eredményeket.');
          } else {
            alert('Import sikeresen befejezve!');
            fetchCollectionsInfo(); // Refresh collections info
          }
        } else {
          const errorData = await response.json();
          alert(`Hiba az import során: ${errorData.detail}`);
        }
      } catch (error) {
        console.error('Import hiba:', error);
        alert('Hiba történt az import során!');
      } finally {
        setLoading(false);
      }
    };

    const toggleCollectionSelection = (collectionName) => {
      setSelectedCollections(prev => 
        prev.includes(collectionName)
          ? prev.filter(name => name !== collectionName)
          : [...prev, collectionName]
      );
    };

    const selectAllCollections = () => {
      setSelectedCollections(Object.keys(collectionsInfo));
    };

    const clearCollectionSelection = () => {
      setSelectedCollections([]);
    };

    return (
      <div>
        <h2 className="text-3xl font-bold text-gray-800 mb-8">Adatmigrációs és Export</h2>
        
        {/* Collections Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Adatbázis Gyűjtemények</h3>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Betöltés...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {Object.entries(collectionsInfo).map(([collectionName, info]) => (
                  <div key={collectionName} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{collectionName}</h4>
                      <input
                        type="checkbox"
                        checked={selectedCollections.includes(collectionName)}
                        onChange={() => toggleCollectionSelection(collectionName)}
                        className="form-checkbox h-4 w-4 text-blue-600"
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      Dokumentumok: <span className="font-medium">{info.count}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Utolsó módosítás: {new Date(info.last_modified).toLocaleDateString('hu-HU')}
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center space-x-4 mb-4">
                <button
                  onClick={selectAllCollections}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Összes kijelölése
                </button>
                <button
                  onClick={clearCollectionSelection}
                  className="text-gray-600 hover:text-gray-800 text-sm"
                >
                  Kijelölés törlése
                </button>
                <span className="text-sm text-gray-500">
                  {selectedCollections.length} / {Object.keys(collectionsInfo).length} kijelölve
                </span>
              </div>
            </>
          )}
        </div>

        {/* Export Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Adatok Exportálása</h3>
          
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              {selectedCollections.length === 0 
                ? 'Válassz gyűjteményeket vagy exportáld az összes adatot' 
                : `${selectedCollections.length} kijelölt gyűjtemény lesz exportálva`}
            </p>
            
            {selectedCollections.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Kijelölt gyűjtemények:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedCollections.map(collection => (
                    <span key={collection} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {collectionsInfo[collection]?.display_name || collection}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleExportData}
              disabled={loading}
              className="primary-button disabled:opacity-50"
            >
              {loading ? 'Exportálás...' : 
                selectedCollections.length === 0 
                  ? 'Teljes Exportálás (Minden Adat)' 
                  : 'Kijelölt Gyűjtemények Exportálása'}
            </button>
            
            {selectedCollections.length > 0 && (
              <button
                onClick={() => handleExportData(true)}
                disabled={loading}
                className="secondary-button disabled:opacity-50 bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? 'Exportálás...' : 'Teljes Export (Minden Adat)'}
              </button>
            )}
          </div>
          
          {exportData && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
              <p className="text-green-800 font-semibold">Export sikeres!</p>
              <p className="text-green-700 text-sm">
                {exportData.export_data.metadata.summary.total_collections} gyűjtemény, {' '}
                {exportData.export_data.metadata.summary.total_records} dokumentum exportálva
              </p>
            </div>
          )}
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Adatok Importálása</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Import fájl kiválasztása (JSON)
              </label>
              <input
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={importSettings.overwriteExisting}
                  onChange={(e) => setImportSettings(prev => ({...prev, overwriteExisting: e.target.checked}))}
                  className="form-checkbox h-4 w-4 text-blue-600 mr-2"
                />
                <span className="text-sm text-gray-700">Meglévő adatok felülírása</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={importSettings.validateOnly}
                  onChange={(e) => setImportSettings(prev => ({...prev, validateOnly: e.target.checked}))}
                  className="form-checkbox h-4 w-4 text-blue-600 mr-2"
                />
                <span className="text-sm text-gray-700">Csak validáció (nem importál adatokat)</span>
              </label>
            </div>

            <button
              onClick={handleImportData}
              disabled={loading || !importFile}
              className="primary-button disabled:opacity-50"
            >
              {loading ? 'Importálás...' : importSettings.validateOnly ? 'Validálás' : 'Importálás'}
            </button>

            {importResults && (
              <div className={`mt-4 p-4 border rounded ${
                importResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <p className={`font-semibold ${importResults.success ? 'text-green-800' : 'text-red-800'}`}>
                  {importResults.success ? 'Import sikeres!' : 'Import hiba!'}
                </p>
                {importResults.summary && (
                  <div className="text-sm mt-2">
                    <p>Importált: {importResults.summary.imported}</p>
                    <p>Frissített: {importResults.summary.updated}</p>
                    <p>Hiba: {importResults.summary.errors}</p>
                  </div>
                )}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-red-800">Hibák:</p>
                    <ul className="text-xs text-red-700 list-disc list-inside">
                      {importResults.errors.slice(0, 5).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {importResults.errors.length > 5 && (
                        <li>... és még {importResults.errors.length - 5} hiba</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper functions for clinic management
  const deleteClinicPermanently = async (clinicId, clinicName) => {
    if (!window.confirm(`Biztos véglegesen törölni szeretné a "${clinicName}" klinikát? Ez a művelet nem vonható vissza!`)) {
      return;
    }
    
    if (!window.confirm('Utoljára kérdezem: VÉGLEGESEN törölni szeretné? Az összes adat elvész!')) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/${clinicId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Klinika véglegesen törölve!');
        fetchAllClinics();
        fetchExpiringClinics();
      } else {
        alert('Hiba a törlés során!');
      }
    } catch (error) {
      console.error('Hiba a törlés során:', error);
      alert('Hiba a törlés során!');
    }
  };

  const updateClinicExpiryDate = async (clinicId, newExpiryDate) => {
    try {
      const formData = new FormData();
      formData.append('expiry_date', newExpiryDate);

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/${clinicId}/expiry-date`, {
        method: 'PUT',
        body: formData
      });

      if (response.ok) {
        alert('Lejárati dátum frissítve!');
        fetchAllClinics();
        fetchExpiringClinics();
      } else {
        alert('Hiba a dátum frissítése során!');
      }
    } catch (error) {
      console.error('Hiba a dátum frissítése során:', error);
      alert('Hiba a dátum frissítése során!');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nincs megadva';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const fetchAllClinics = async () => {
    try {
      // Build query parameters for admin clinic search
      const params = new URLSearchParams();
      if (clinicSearch) params.append('search', clinicSearch);
      params.append('sort_by', clinicSortBy);
      params.append('order', clinicSortOrder);
      params.append('limit', '100');
      
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics?${params.toString()}`);
      const data = await response.json();
      
      // Ensure data is an array before setting state
      if (Array.isArray(data)) {
        setAllClinics(data);
      } else {
        console.error('Admin clinics API returned non-array data:', data);
        setAllClinics([]); // Fallback to empty array
      }
    } catch (error) {
      console.error('Hiba a klinikák betöltése során:', error);
      // Fallback to original endpoint if admin endpoint fails
      try {
        const response = await fetch(`${backendUrl}/api/clinics?include_inactive=true`);
        const data = await response.json();
        
        // Ensure fallback data is also an array
        if (Array.isArray(data)) {
          setAllClinics(data);
        } else {
          console.error('Fallback clinics API returned non-array data:', data);
          setAllClinics([]); // Fallback to empty array
        }
      } catch (fallbackError) {
        console.error('Hiba az eredeti endpoint használata során:', fallbackError);
        setAllClinics([]); // Ensure allClinics is always an array
      }
    }
  };

  // Bulk operations functions
  const handleSelectAll = () => {
    if (selectedClinicIds.length === paginatedClinics.length) {
      // Deselect all
      setSelectedClinicIds([]);
    } else {
      // Select all on current page
      setSelectedClinicIds(paginatedClinics.map(clinic => clinic.id));
    }
  };

  const handleSelectClinic = (clinicId) => {
    if (selectedClinicIds.includes(clinicId)) {
      setSelectedClinicIds(selectedClinicIds.filter(id => id !== clinicId));
    } else {
      setSelectedClinicIds([...selectedClinicIds, clinicId]);
    }
  };

  const handleBulkOperation = () => {
    if (selectedClinicIds.length === 0) {
      alert('Kérem válasszon legalább egy klinikát!');
      return;
    }
    if (!bulkOperation) {
      alert('Kérem válasszon műveletet!');
      return;
    }
    setShowBulkConfirm(true);
  };

  const executeBulkOperation = async () => {
    if (bulkOperationInProgress) return;
    
    setBulkOperationInProgress(true);
    
    try {
      const operationLabels = {
        'delete': 'végleges törlés',
        'archive': 'archiválás',
        'activate': 'aktiválás', 
        'feature': 'kiemelés',
        'unfeature': 'kiemelt levétele'
      };
      
      const operation = bulkOperation === 'activate' ? 'activate' : bulkOperation;
      
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/bulk-operation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clinic_ids: selectedClinicIds,
          operation: operation
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert(`${data.affected_count} klinika sikeresen ${data.message.split(' sikeresen ')[1]}`);
        setSelectedClinicIds([]);
        setBulkOperation('');
        setShowBulkConfirm(false);
        fetchAllClinics();
        fetchExpiringClinics();
      } else {
        alert(`Hiba: ${data.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba a tömeges művelet során:', error);
      alert('Hiba a tömeges művelet során!');
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const cancelBulkOperation = () => {
    setShowBulkConfirm(false);
    setBulkOperation('');
  };

  const getBulkOperationMessage = () => {
    const operationLabels = {
      'delete': 'véglegesen törölni',
      'archive': 'archiválni',
      'activate': 'aktiválni',
      'feature': 'kiemelni',
      'unfeature': 'kiemelt státuszát levenni'
    };
    
    return `Biztos ${operationLabels[bulkOperation]} szeretné a kiválasztott ${selectedClinicIds.length} klinikát?`;
  };

  // Pagination calculations for clinics
  // Ensure allClinics is an array to prevent slice() errors
  const clinicsArray = Array.isArray(allClinics) ? allClinics : [];
  const totalPages = Math.ceil(clinicsArray.length / clinicsPerPage);
  const startIndex = (clinicsPage - 1) * clinicsPerPage;
  const endIndex = startIndex + clinicsPerPage;
  const paginatedClinics = clinicsArray.slice(startIndex, endIndex);

  // Functions for external links
  const openClinicWebsite = (website) => {
    if (website) {
      // Ensure URL has protocol
      const url = website.startsWith('http') ? website : `https://${website}`;
      window.open(url, '_blank');
    }
  };

  const openClinicProfile = (clinic) => {
    // Generate SEO-friendly URL and open in new tab
    const seoSlug = generateClinicSlug(clinic);
    const baseUrl = window.location.origin;
    const clinicUrl = `${baseUrl}/clinic/${seoSlug}`;
    window.open(clinicUrl, '_blank');
  };

  const fetchExpiringClinics = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/expiring-soon?days=30`);
      const data = await response.json();
      setExpiringClinics(data);
    } catch (error) {
      console.error('Hiba a lejáró klinikák betöltése során:', error);
    }
  };

  const fetchAllBlogPosts = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog?limit=100`);
      const data = await response.json();
      setAllBlogPosts(data);
    } catch (error) {
      console.error('Hiba a blogbejegyzések betöltése során:', error);
    }
  };

  const fetchBlogCategories = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/blog/categories`);
      const data = await response.json();
      setBlogCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Hiba a kategóriák betöltése során:', error);
      setBlogCategories([]);
    }
  };

  const fetchAllPartners = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/partners`);
      const data = await response.json();
      setAllPartners(data);
    } catch (error) {
      console.error('Hiba a partnerek betöltése során:', error);
    }
  };

  // Settings functions
  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/settings`);
      const data = await response.json();
      setSettings({
        ga4_tracking_id: data.ga4_tracking_id || '',
        default_og_image: data.default_og_image || '',
        visitor_stats_image: data.visitor_stats_image || '',
        monthly_visitors: data.monthly_visitors || ''
      });
    } catch (error) {
      console.error('Hiba a beállítások betöltése során:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Testimonials functions
  const fetchTestimonials = async () => {
    try {
      setTestimonialsLoading(true);
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/testimonials`);
      const data = await response.json();
      setTestimonials(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Hiba a vélemények betöltése során:', error);
      setTestimonials([]);
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const createTestimonial = async () => {
    if (!newTestimonial.name || !newTestimonial.clinic_name || !newTestimonial.content) {
      alert('Kérjük töltse ki az összes kötelező mezőt!');
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/testimonials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTestimonial)
      });

      if (response.ok) {
        const data = await response.json();
        setTestimonials(prev => [data, ...prev]);
        setNewTestimonial({
          rating: 5,
          name: '',
          clinic_name: '',
          content: ''
        });
        alert('Vélemény sikeresen hozzáadva!');
      } else {
        const errorData = await response.json();
        alert(`Hiba: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Hiba a vélemény létrehozása során:', error);
      alert('Hiba történt a vélemény létrehozása során!');
    }
  };

  const updateTestimonial = async (testimonialId, updateData) => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/testimonials/${testimonialId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const data = await response.json();
        setTestimonials(prev => prev.map(t => t.id === testimonialId ? data : t));
        setEditingTestimonial(null);
        alert('Vélemény sikeresen frissítve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Hiba a vélemény frissítése során:', error);
      alert('Hiba történt a vélemény frissítése során!');
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a véleményt?')) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/testimonials/${testimonialId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setTestimonials(prev => prev.filter(t => t.id !== testimonialId));
        alert('Vélemény sikeresen törölve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Hiba a vélemény törlése során:', error);
      alert('Hiba történt a vélemény törlése során!');
    }
  };

  const handleTestimonialImageUpload = async (testimonialId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete nem lehet nagyobb 5MB-nál!');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      alert('Csak JPEG, PNG és WebP képformátumok engedélyezettek!');
      return;
    }

    try {
      setTestimonialImageUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/uploads/testimonial/${testimonialId}/image`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setTestimonials(prev => prev.map(t => 
          t.id === testimonialId ? { ...t, image: data.image_url } : t
        ));
        alert('Kép sikeresen feltöltve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba a kép feltöltése során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba a kép feltöltése során:', error);
      alert('Hiba történt a kép feltöltése során!');
    } finally {
      setTestimonialImageUploading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
        alert('Beállítások sikeresen mentve!');
      } else {
        alert('Hiba a beállítások mentése során!');
      }
    } catch (error) {
      console.error('Hiba a beállítások mentése során:', error);
      alert('Hiba a beállítások mentése során!');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleVisitorStatsImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete túl nagy! Maximum 5MB engedélyezett.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Csak JPEG, PNG és WebP képformátumok engedélyezettek!');
      return;
    }

    try {
      setVisitorStatsImageUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/uploads/visitor-stats/image`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          visitor_stats_image: data.url
        }));
        alert('Látogatottság statisztika kép sikeresen feltöltve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba a kép feltöltése során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba a látogatottság kép feltöltése során:', error);
      alert('Hiba történt a kép feltöltése során!');
    } finally {
      setVisitorStatsImageUploading(false);
    }
  };

  const handleOgImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete túl nagy! Maximum 5MB engedélyezett.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Csak JPEG, PNG, GIF és WebP képformátumok engedélyezettek!');
      return;
    }

    try {
      setOgImageUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/uploads/og/image`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          default_og_image: data.url
        }));
        alert('OpenGraph kép sikeresen feltöltve!');
      } else {
        const errorData = await response.json();
        alert(`Hiba a kép feltöltése során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba az OG kép feltöltése során:', error);
      alert('Hiba történt a kép feltöltése során!');
    } finally {
      setOgImageUploading(false);
    }
  };

  const handleUpdatePartner = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await makeAuthenticatedRequest(
        `${backendUrl}/api/admin/partners/${editingPartner.id}`, 
        {
          method: 'PUT',
          body: formData
        }
      );
      if (response.ok) {
        alert('Partner sikeresen frissítve!');
        setEditingPartner(null);
        setPartnerFormData({ name: '', website: '', facebook_url: '', instagram_url: '', linkedin_url: '', order: 1 });
        fetchAllPartners();
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba történt a partner frissítése során.');
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/partners`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        alert('Partner sikeresen hozzáadva!');
        e.target.reset();
        setPartnerFormData({ name: '', website: '', facebook_url: '', instagram_url: '', linkedin_url: '', order: 1 });
        fetchAllPartners();
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba történt a partner hozzáadása során.');
    }
  };

  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    setPartnerFormData({
      name: partner.name || '',
      website: partner.website || '',
      facebook_url: partner.facebook_url || '',
      instagram_url: partner.instagram_url || '',
      linkedin_url: partner.linkedin_url || '',
      order: partner.order || 1
    });
  };

  const handleCancelEdit = () => {
    setEditingPartner(null);
    setPartnerFormData({ name: '', website: '', facebook_url: '', instagram_url: '', linkedin_url: '', order: 1 });
  };

  const togglePartnerActive = async (partnerId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${backendUrl}/api/admin/partners/${partnerId}/toggle-active`, 
        { method: 'PATCH' }
      );
      if (response.ok) {
        fetchAllPartners();
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba történt a partner állapotának változtatása során.');
    }
  };

  const deletePartner = async (partnerId) => {
    if (confirm('Biztosan törölni szeretné ezt a partnert?')) {
      try {
        const response = await makeAuthenticatedRequest(
          `${backendUrl}/api/admin/partners/${partnerId}`, 
          { method: 'DELETE' }
        );
        if (response.ok) {
          alert('Partner sikeresen törölve!');
          fetchAllPartners();
        }
      } catch (error) {
        console.error('Hiba:', error);
        alert('Hiba történt a partner törlése során.');
      }
    }
  };

  const handleCreateClinic = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(newClinic).forEach(key => {
        if (key === 'services') {
          formData.append(key, newClinic[key].join(','));
        } else {
          formData.append(key, newClinic[key]);
        }
      });
      
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      selectedFiles.forEach((file, index) => {
        formData.append('images', file);
      });

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Klinika sikeresen létrehozva!');
        setNewClinic({
          name: '', city: '', district: '', phone: '', email: '', address: '', website: '',
          facebook_url: '', instagram_url: '', linkedin_url: '', youtube_url: '', intro_video_url: '', foglaljorvost_link: '', google_business_link: '',
          services: [], opening_hours: '', description: '', subscription_start: '', subscription_end: '',
          has_subpage: false, subpage_url: '',
          // SEO mezők reset
          meta_title: '', meta_description: '', og_image: ''
        });
        setSelectedFiles([]);
        setLogoFile(null);
        fetchAllClinics();
      } else {
        alert('Hiba a klinika létrehozása során!');
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba a klinika létrehozása során!');
    }
  };

  const handleCreateBlogPost = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(newBlogPost).forEach(key => {
        if (key === 'categories' || key === 'tags') {
          formData.append(key, newBlogPost[key].join(','));
        } else {
          formData.append(key, newBlogPost[key]);
        }
      });

      if (featuredImageFile) {
        formData.append('featured_image', featuredImageFile);
      }

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Blogbejegyzés sikeresen létrehozva!');
        setNewBlogPost({ 
          title: '', content: '', excerpt: '', author: '', status: 'draft',
          categories: [], tags: [], meta_title: '', meta_description: '', published_at: ''
        });
        setFeaturedImageFile(null);
        fetchAllBlogPosts();
      } else {
        alert('Hiba a blogbejegyzés létrehozása során!');
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba a blogbejegyzés létrehozása során!');
    }
  };

  const startEditPost = (post) => {
    setEditingPost(post.id);
    setEditBlogPost({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      author: post.author,
      status: post.status,
      categories: post.categories || [],
      tags: post.tags || [],
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      published_at: post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : ''
    });
  };

  const cancelEditPost = () => {
    setEditingPost(null);
    setEditBlogPost({
      title: '', content: '', excerpt: '', author: '', status: 'draft',
      categories: [], tags: [], meta_title: '', meta_description: '', published_at: ''
    });
    setEditFeaturedImageFile(null);
  };

  const handleUpdateBlogPost = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(editBlogPost).forEach(key => {
        if (key === 'categories' || key === 'tags') {
          formData.append(key, editBlogPost[key].join(','));
        } else {
          formData.append(key, editBlogPost[key]);
        }
      });

      if (editFeaturedImageFile) {
        formData.append('featured_image', editFeaturedImageFile);
      }

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog/${editingPost}`, {
        method: 'PUT',
        body: formData
      });

      if (response.ok) {
        alert('Blogbejegyzés sikeresen frissítve!');
        cancelEditPost();
        fetchAllBlogPosts();
      } else {
        alert('Hiba a blogbejegyzés frissítése során!');
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba a blogbejegyzés frissítése során!');
    }
  };

  const toggleCategoryForPost = (category, isNew = true) => {
    const setter = isNew ? setNewBlogPost : setEditBlogPost;
    const current = isNew ? newBlogPost : editBlogPost;
    
    setter(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const createBlogCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Kérem adjon meg kategória nevet!');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', newCategoryName.trim());
      formData.append('description', newCategoryDescription.trim());

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog/categories`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const newCategory = await response.json();
        setBlogCategories(prev => [...prev, newCategory]);
        setNewCategoryName('');
        setNewCategoryDescription('');
        alert('Kategória sikeresen létrehozva!');
      } else {
        alert('Hiba a kategória létrehozása során!');
      }
    } catch (error) {
      console.error('Hiba a kategória létrehozása során:', error);
      alert('Hiba a kategória létrehozása során!');
    }
  };

  const addQuickCategory = async (categoryName, isNew = true) => {
    if (!categoryName.trim()) return;
    
    // Check if category already exists
    if (blogCategories.some(cat => cat.name.toLowerCase() === categoryName.toLowerCase())) {
      // If exists, just add it to the post
      toggleCategoryForPost(categoryName, isNew);
      return;
    }

    // Create new category
    try {
      const formData = new FormData();
      formData.append('name', categoryName.trim());
      formData.append('description', '');

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog/categories`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const newCategory = await response.json();
        setBlogCategories(prev => [...prev, newCategory]);
        
        // Add to current post
        toggleCategoryForPost(categoryName, isNew);
        
        console.log(`Új kategória létrehozva és hozzáadva: ${categoryName}`);
      } else {
        console.error('Hiba a kategória létrehozása során');
      }
    } catch (error) {
      console.error('Hiba a kategória létrehozása során:', error);
    }
  };

  const deleteBlogCategory = async (categoryId, categoryName) => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog/categories/${categoryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Remove category from the list
        setBlogCategories(prev => prev.filter(cat => cat.id !== categoryId));
        alert(`A "${categoryName}" kategória sikeresen törölve!`);
      } else {
        const errorData = await response.json();
        alert(`Hiba a kategória törlése során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba a kategória törlése során:', error);
      alert('Hiba a kategória törlése során!');
    }
  };

  const addTagToPost = (tag, isNew = true) => {
    const setter = isNew ? setNewBlogPost : setEditBlogPost;
    
    setter(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags : [...prev.tags, tag]
    }));
  };

  const removeTagFromPost = (tag, isNew = true) => {
    const setter = isNew ? setNewBlogPost : setEditBlogPost;
    
    setter(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleDeleteBlogPost = async (postId, postTitle) => {
    // Double confirmation for permanent deletion
    if (!window.confirm(`Biztosan törölni szeretné a "${postTitle}" című blogbejegyzést? Ez a művelet nem visszavonható!`)) {
      return;
    }
    
    if (!window.confirm("Véglegesen törli ezt a blogbejegyzést? Az összes tartalom elvész!")) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/blog/${postId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Blogbejegyzés sikeresen törölve!');
        // Refresh the blog posts list
        fetchAllBlogPosts();
      } else {
        alert('Hiba a blogbejegyzés törlése során!');
      }
    } catch (error) {
      console.error('Hiba a törlés során:', error);
      alert('Hiba a blogbejegyzés törlése során!');
    }
  };

  const toggleFeatured = async (clinicId, featured) => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/${clinicId}/featured?featured=${!featured}`, {
        method: 'PUT'
      });
      if (response.ok) {
        fetchAllClinics();
      }
    } catch (error) {
      console.error('Hiba a kiemelt státusz változtatása során:', error);
    }
  };

  const toggleActive = async (clinicId, active) => {
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/${clinicId}/active?active=${!active}`, {
        method: 'PUT'
      });
      if (response.ok) {
        fetchAllClinics();
      }
    } catch (error) {
      console.error('Hiba az aktív státusz változtatása során:', error);
    }
  };

  const startEditClinic = (clinic) => {
    setEditingClinic(clinic.id);
    setEditClinic({
      name: clinic.name,
      city: clinic.city,
      district: clinic.district,
      phone: clinic.phone,
      email: clinic.email,
      address: clinic.address,
      website: clinic.website || '',
      facebook_url: clinic.facebook_url || '',
      instagram_url: clinic.instagram_url || '',
      linkedin_url: clinic.linkedin_url || '',
      youtube_url: clinic.youtube_url || '',
      intro_video_url: clinic.intro_video_url || '',
      foglaljorvost_link: clinic.foglaljorvost_link || '',
      google_business_link: clinic.google_business_link || '',
      services: clinic.services,
      opening_hours: clinic.opening_hours,
      description: clinic.description,
      subscription_start: clinic.subscription_start,
      subscription_end: clinic.subscription_end,
      expiry_date: clinic.expiry_date || '',
      has_subpage: clinic.has_subpage || false,
      subpage_url: clinic.subpage_url || '',
      // SEO mezők
      meta_title: clinic.meta_title || '',
      meta_description: clinic.meta_description || '',
      og_image: clinic.og_image || '',
      // Képek betöltése
      logo: clinic.logo || '',
      images: clinic.images || []
    });
    // Reset deletion states
    setImagesToDelete([]);
    setDeleteCurrentLogo(false);
    setEditLogoFile(null);
    setEditSelectedFiles([]);
  };

  const cancelEditClinic = () => {
    setEditingClinic(null);
    setEditClinic({
      name: '', city: '', district: '', phone: '', email: '', address: '', website: '',
      facebook_url: '', instagram_url: '', linkedin_url: '', youtube_url: '', intro_video_url: '', foglaljorvost_link: '', google_business_link: '',
      services: [], opening_hours: '', description: '', subscription_start: '', subscription_end: '',
      expiry_date: '', has_subpage: false, subpage_url: '',
      // SEO mezők reset
      meta_title: '', meta_description: '', og_image: '',
      // Képek reset
      logo: '', images: []
    });
    // Reset image states
    setEditLogoFile(null);
    setEditSelectedFiles([]);
    setImagesToDelete([]);
    setDeleteCurrentLogo(false);
  };

  // Handle removing existing gallery image
  const handleRemoveGalleryImage = (imageUrl) => {
    // Add to delete list
    setImagesToDelete(prev => [...prev, imageUrl]);
    // Remove from editClinic.images
    setEditClinic(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imageUrl)
    }));
  };

  // Handle removing logo
  const handleRemoveLogo = () => {
    setDeleteCurrentLogo(true);
    setEditClinic(prev => ({...prev, logo: ''}));
  };

  const handleUpdateClinic = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      
      // Add all fields except logo and images (those are handled separately)
      Object.keys(editClinic).forEach(key => {
        // Skip logo and images - they're handled separately
        if (key === 'logo' || key === 'images') {
          return;
        }
        
        if (key === 'services') {
          formData.append(key, editClinic[key].join(','));
        } else {
          formData.append(key, editClinic[key]);
        }
      });

      // Add logo file if selected
      if (editLogoFile) {
        formData.append('logo', editLogoFile);
      }
      // Send delete logo flag
      if (deleteCurrentLogo) {
        formData.append('delete_logo', 'true');
      }
      
      // Add gallery images if selected
      editSelectedFiles.forEach((file, index) => {
        formData.append('images', file);
      });
      
      // Send images to delete
      if (imagesToDelete.length > 0) {
        formData.append('images_to_delete', JSON.stringify(imagesToDelete));
      }

      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/${editingClinic}`, {
        method: 'PUT',
        body: formData
      });

      if (response.ok) {
        alert('Klinika sikeresen frissítve!');
        cancelEditClinic();
        fetchAllClinics();
      } else {
        const errorData = await response.json();
        console.error('Update error:', errorData);
        alert('Hiba a klinika frissítése során!');
      }
    } catch (error) {
      console.error('Hiba:', error);
      alert('Hiba a klinika frissítése során!');
    }
  };

  const handleEditServiceToggle = (service) => {
    setEditClinic(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleServiceToggle = (service) => {
    setNewClinic(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  // Import functionality functions - TEMPORARILY NON-MEMOIZED to fix admin interface
  const fetchClinicFields = async () => {
    try {
      console.log('🎯 fetchClinicFields started...');
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinic-data/fields`);
      console.log('🎯 fetchClinicFields response status:', response.status);
      
      if (!response.ok) {
        console.log('🎯 fetchClinicFields failed with status:', response.status);
        console.log('🎯 THIS MIGHT BE CAUSING THE ADMINVIEW RESET!');
        return;
      }
      
      const data = await response.json();
      console.log('🎯 fetchClinicFields data:', data);
      
      // Ensure we set the clinic_fields array from the response
      if (data && data.clinic_fields && Array.isArray(data.clinic_fields)) {
        setClinicFields(data.clinic_fields);
        console.log('🎯 fetchClinicFields completed successfully with', data.clinic_fields.length, 'fields');
      } else {
        console.error('🎯 fetchClinicFields: Invalid response format - expected clinic_fields array');
        setClinicFields([]); // Fallback to empty array
      }
      
      // Set transformations array from the response
      if (data && data.transformations && Array.isArray(data.transformations)) {
        setTransformations(data.transformations);
        console.log('🎯 fetchClinicFields completed successfully with', data.transformations.length, 'transformations');
      } else {
        console.error('🎯 fetchClinicFields: Invalid response format - expected transformations array');
        setTransformations([]); // Fallback to empty array
      }
    } catch (error) {
      console.error('🎯 fetchClinicFields ERROR:', error);
      console.log('🎯 ERROR MIGHT BE CAUSING ADMINVIEW RESET!');
    }
  };

  const analyzeImportFile = async () => {
    if (!importFile && !importUrl) {
      alert('Kérem válasszon fájlt vagy adjon meg URL-t!');
      return;
    }

    console.log('🔍 Import analysis starting...');
    console.log('🔍 Backend URL:', backendUrl);
    console.log('🔍 Admin Token present:', !!adminToken);
    console.log('🔍 Import URL:', importUrl);

    setImportLoading(true);
    try {
      const formData = new FormData();
      if (importFile) {
        formData.append('file', importFile);
      }
      if (importUrl) {
        formData.append('file_url', importUrl);
      }

      console.log('🔍 Making request to:', `${backendUrl}/api/admin/clinics/process-data`);
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinics/process-data`, {
        method: 'POST',
        body: formData
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);

      if (response.ok) {
        const schema = await response.json();
        console.log('🔍 Schema received:', schema);
        setImportSchema(schema);
        setImportStep(2);
        // Initialize mappings
        const initialMappings = schema.fields.map(field => ({
          source_field: field.name || field,
          target_field: '',
          transformation: 'direct'
        }));
        setImportMappings(initialMappings);
      } else {
        const errorText = await response.text();
        console.error('🔍 Error response:', errorText);
        try {
          const error = JSON.parse(errorText);
          alert(`Hiba a fájl elemzése során: ${error.detail}`);
        } catch {
          alert(`Hiba a fájl elemzése során: ${response.status} ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('🔍 Network error:', error);
      
      // Ad blocker bypass attempt
      if (error.message && error.message.includes('Failed to fetch')) {
        alert('🚫 Az ad blocker blokkolja a kérést. Kérem kapcsolja ki az ad blockert vagy engedélyezze ezt az oldalt, majd próbálja újra.');
      } else {
        alert('Hálózati hiba a fájl elemzése során!');
      }
    } finally {
      setImportLoading(false);
    }
  };

  const updateMapping = (index, field, value) => {
    const newMappings = [...importMappings];
    newMappings[index][field] = value;
    setImportMappings(newMappings);
  };

  const addAdditionalMapping = (sourceField) => {
    const newMapping = {
      source_field: sourceField,
      target_field: '',
      transformation: 'direct'
    };
    setImportMappings([...importMappings, newMapping]);
  };

  const removeMapping = (index) => {
    const newMappings = importMappings.filter((_, i) => i !== index);
    setImportMappings(newMappings);
  };

  const previewImport = async () => {
    setImportLoading(true);
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinic-data/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_url: importUrl || importSchema.temp_file_path,
          mappings: importMappings,
          import_settings: {}
        })
      });

      if (response.ok) {
        const preview = await response.json();
        setImportPreview(preview);
        setImportStep(3);
      } else {
        const error = await response.json();
        alert(`Hiba az előnézet generálása során: ${error.detail}`);
      }
    } catch (error) {
      console.error('Hiba az előnézet során:', error);
      alert('Hiba az előnézet során!');
    } finally {
      setImportLoading(false);
    }
  };

  const executeImport = async () => {
    setImportLoading(true);
    try {
      const response = await makeAuthenticatedRequest(`${backendUrl}/api/admin/clinic-data/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_url: importUrl || importSchema.temp_file_path,
          mappings: importMappings,
          import_settings: {},
          preview_confirmed: true
        })
      });

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        setImportStep(4);
        // Refresh clinics list
        fetchAllClinics();
      } else {
        const error = await response.json();
        alert(`Hiba az import végrehajtása során: ${error.detail}`);
      }
    } catch (error) {
      console.error('Hiba az import során:', error);
      alert('Hiba az import során!');
    } finally {
      setImportLoading(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportUrl('');
    setImportSchema(null);
    setImportMappings([]);
    setImportPreview(null);
    setImportResult(null);
    setImportStep(1);
  };

  // Legal Pages Management Functions
  const loadLegalPages = async () => {
    try {
      // Load privacy page
      const privacyResponse = await fetch(`${backendUrl}/api/legal/privacy`);
      if (privacyResponse.ok) {
        const privacyData = await privacyResponse.json();
        setLegalPages(prev => ({
          ...prev,
          privacy: {
            title: privacyData.title || '',
            content: privacyData.content || ''
          }
        }));
      }

      // Load terms page
      const termsResponse = await fetch(`${backendUrl}/api/legal/terms`);
      if (termsResponse.ok) {
        const termsData = await termsResponse.json();
        setLegalPages(prev => ({
          ...prev,
          terms: {
            title: termsData.title || '',
            content: termsData.content || ''
          }
        }));
      }
    } catch (error) {
      console.error('Hiba a jogi oldalak betöltése során:', error);
    }
  };

  const saveLegalPage = async (pageType) => {
    try {
      setLegalPagesLoading(true);
      setLegalPagesSaved(false);

      const pageData = legalPages[pageType];
      const response = await makeAuthenticatedRequest(
        `${backendUrl}/api/admin/legal/${pageType}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: pageData.title,
            content: pageData.content
          })
        }
      );

      if (response.ok) {
        setLegalPagesSaved(true);
        setTimeout(() => setLegalPagesSaved(false), 3000);
        alert(`${pageType === 'privacy' ? 'Adatvédelmi tájékoztató' : 'ÁSZF'} sikeresen mentve!`);
      } else {
        const errorData = await response.json();
        alert(`Hiba a mentés során: ${errorData.detail || 'Ismeretlen hiba'}`);
      }
    } catch (error) {
      console.error('Hiba a jogi oldal mentése során:', error);
      alert('Hiba történt a mentés során!');
    } finally {
      setLegalPagesLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-8">
              <h1 className="text-2xl font-bold text-blue-600">FogPortál.hu Admin</h1>
              <nav className="flex space-x-6">
                <button 
                  onClick={() => setAdminView('dashboard')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'dashboard' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Irányítópult
                </button>
                <button 
                  onClick={() => setAdminView('clinics')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'clinics' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Klinikák
                </button>
                <button 
                  onClick={() => setAdminView('crm')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'crm' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  CRM
                </button>
                <button 
                  onClick={() => setAdminView('blog')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'blog' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Blog
                </button>
                <button 
                  onClick={() => setAdminView('partners')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'partners' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Partnerek
                </button>
                <button 
                  onClick={() => setAdminView('messages')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'messages' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Üzenetek
                </button>
                <button 
                  onClick={() => {
                    setAdminView('import');
                    // Manually load clinic fields when entering import view
                    fetchClinicFields();
                  }}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'import' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Klinika Import
                </button>
                <button 
                  onClick={() => setAdminView('widget')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'widget' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Widget
                </button>
                <button 
                  onClick={() => setAdminView('migration')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'migration' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Adatmigrációs
                </button>
                <button 
                  onClick={() => setAdminView('settings')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminView === 'settings' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600'}`}
                >
                  Beállítások
                </button>
              </nav>
            </div>
            <button 
              onClick={handleAdminLogout}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
            >
              Kijelentkezés
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard View */}
        {adminView === 'dashboard' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Irányítópult</h2>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-800">Összes klinika</h3>
                <p className="text-3xl font-bold text-blue-600">{clinicsArray.length}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-800">Kiemelt klinikák</h3>
                <p className="text-3xl font-bold text-green-600">{clinicsArray.filter(c => c.featured).length}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-800">Blogbejegyzések</h3>
                <p className="text-3xl font-bold text-purple-600">{allBlogPosts.length}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-800">Partnerek</h3>
                <p className="text-3xl font-bold text-orange-600">{allPartners.length}</p>
              </div>
            </div>

            {/* Expiring Subscriptions */}
            {expiringClinics.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-red-800 mb-4">
                  ⚠️ Lejáró előfizetések (30 napon belül)
                </h3>
                <div className="space-y-3">
                  {expiringClinics.map(clinic => (
                    <div key={clinic.id} className="flex justify-between items-center bg-white p-3 rounded">
                      <div>
                        <span className="font-medium">{clinic.name}</span>
                        <span className="text-gray-600 ml-2">({clinic.city})</span>
                      </div>
                      <span className="text-red-600 font-medium">
                        Lejár: {new Date(clinic.subscription_end).toLocaleDateString('hu-HU')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rest of the admin panel components remain the same */}
        {/* Clinics Management */}
        {adminView === 'clinics' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Klinikák kezelése</h2>
            
            {/* Hamarosan lejáró előfizetések */}
            {expiringClinics.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">⚠️</span>
                  Hamarosan lejáró előfizetések ({expiringClinics.length} db)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {expiringClinics.map(clinic => {
                    const daysLeft = getDaysUntilExpiry(clinic.expiry_date);
                    return (
                      <div key={clinic.id} className="bg-white border-l-4 border-red-500 p-4 rounded shadow">
                        <div className="font-semibold text-gray-800">{clinic.name}</div>
                        <div className="text-sm text-gray-600">{clinic.city}</div>
                        <div className="text-red-600 font-medium mt-2">
                          {daysLeft !== null && daysLeft <= 0 ? (
                            <span className="text-red-800 font-bold">LEJÁRT!</span>
                          ) : daysLeft !== null ? (
                            <span>{daysLeft} nap múlva lejár</span>
                          ) : (
                            'Nincs lejárati dátum'
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(clinic.expiry_date)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Keresés és rendezés */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Keresés és szűrés</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Klinika neve..."
                  value={clinicSearch}
                  onChange={(e) => setClinicSearch(e.target.value)}
                  className="form-input"
                />
                <select
                  value={clinicSortBy}
                  onChange={(e) => setClinicSortBy(e.target.value)}
                  className="form-input"
                >
                  <option value="name">Rendezés név szerint</option>
                  <option value="city">Rendezés város szerint</option>
                  <option value="expiry_date">Rendezés lejárati dátum szerint</option>
                </select>
                <select
                  value={clinicSortOrder}
                  onChange={(e) => setClinicSortOrder(e.target.value)}
                  className="form-input"
                >
                  <option value="asc">Növekvő sorrend</option>
                  <option value="desc">Csökkenő sorrend</option>
                </select>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                Találatok száma: {clinicsArray.length} db
              </div>
            </div>
            
            {/* Add New Clinic Form */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Új klinika hozzáadása</h3>
              <form onSubmit={handleCreateClinic} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Klinika neve"
                    value={newClinic.name}
                    onChange={(e) => setNewClinic(prev => ({...prev, name: e.target.value}))}
                    className="form-input"
                    required
                  />
                  <CityAutocomplete
                    value={newClinic.city}
                    onChange={(value) => {
                      const district = extractDistrictFromCity(value);
                      setNewClinic(prev => ({
                        ...prev, 
                        city: value,
                        district: district
                      }));
                    }}
                    placeholder="Város keresése..."
                    required={true}
                  />
                  <input
                    type="tel"
                    placeholder="Telefon"
                    value={newClinic.phone}
                    onChange={(e) => setNewClinic(prev => ({...prev, phone: e.target.value}))}
                    className="form-input"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newClinic.email}
                    onChange={(e) => setNewClinic(prev => ({...prev, email: e.target.value}))}
                    className="form-input"
                    required
                  />
                  <input
                    type="url"
                    placeholder="Weboldal (pl. https://klinika.hu)"
                    value={newClinic.website}
                    onChange={(e) => setNewClinic(prev => ({...prev, website: e.target.value}))}
                    className="form-input"
                  />
                </div>
                
                {/* Social Media Links Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">Social Media Linkek</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="url"
                      placeholder="Facebook URL (opcionális)"
                      value={newClinic.facebook_url}
                      onChange={(e) => setNewClinic(prev => ({...prev, facebook_url: e.target.value}))}
                      className="form-input"
                    />
                    <input
                      type="url"
                      placeholder="Instagram URL (opcionális)"
                      value={newClinic.instagram_url}
                      onChange={(e) => setNewClinic(prev => ({...prev, instagram_url: e.target.value}))}
                      className="form-input"
                    />
                    <input
                      type="url"
                      placeholder="LinkedIn URL (opcionális)"
                      value={newClinic.linkedin_url}
                      onChange={(e) => setNewClinic(prev => ({...prev, linkedin_url: e.target.value}))}
                      className="form-input"
                    />
                    <input
                      type="url"
                      placeholder="YouTube csatorna URL (opcionális)"
                      value={newClinic.youtube_url}
                      onChange={(e) => setNewClinic(prev => ({...prev, youtube_url: e.target.value}))}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Intro Video Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">📹 Bemutatkozó Videó</h4>
                  <input
                    type="url"
                    placeholder="YouTube videó URL (pl: https://youtu.be/6zHLt1q8h1Q)"
                    value={newClinic.intro_video_url}
                    onChange={(e) => setNewClinic(prev => ({...prev, intro_video_url: e.target.value}))}
                    className="form-input"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    A bemutatkozó videó a klinika profil oldalon jelenik meg beágyazva.
                  </p>
                </div>

                {/* Foglaljorvost Link Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">⏰ Online Időpontfoglalás</h4>
                  <div className="bg-green-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-green-800 mb-2">
                      🌐 <strong>Foglaljorvost.hu integráció:</strong> Adja meg a Foglaljorvost.hu profil linkjét
                    </p>
                    <p className="text-xs text-green-600">
                      Ha van Foglaljorvost.hu profilja, a páciensek közvetlenül online foglalhatnak időpontot.
                    </p>
                  </div>
                  <input
                    type="url"
                    placeholder="Foglaljorvost.hu profil link (pl: https://foglaljorvost.hu/orvos/...)"
                    value={newClinic.foglaljorvost_link}
                    onChange={(e) => setNewClinic(prev => ({...prev, foglaljorvost_link: e.target.value}))}
                    className="form-input"
                  />
                </div>

                {/* Google Business Link Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">🌟 Google Értékelések</h4>
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-blue-800 mb-2">
                      📍 <strong>Google Business profilhoz csatlakoztatás:</strong> Adja meg a rövidített Google Maps linket (pl. https://g.co/kgs/RyzNpd6)
                    </p>
                    <p className="text-xs text-blue-600">
                      A rendszer automatikusan lekéri és megjeleníti a Google értékeléseket a klinikájáról.
                    </p>
                  </div>
                  <input
                    type="url"
                    placeholder="Google Business Link (pl. https://g.co/kgs/RyzNpd6)"
                    value={newClinic.google_business_link || ''}
                    onChange={(e) => setNewClinic(prev => ({...prev, google_business_link: e.target.value}))}
                    className="form-input w-full"
                  />
                </div>

                {/* SEO Beállítások Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">🔍 SEO Beállítások</h4>
                  <div className="bg-purple-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-purple-800 mb-2">
                      📈 <strong>SEO optimalizálás:</strong> Ezek a mezők javítják a Google találati helyezést és a social media megjelenést
                    </p>
                    <p className="text-xs text-purple-600">
                      Ha üresen hagyja, a rendszer automatikusan generálja a klinika neve és leírása alapján.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Meta Title (SEO cím - max 60 karakter)"
                      value={newClinic.meta_title || ''}
                      onChange={(e) => setNewClinic(prev => ({...prev, meta_title: e.target.value}))}
                      className="form-input w-full"
                      maxLength="60"
                    />
                    <textarea
                      placeholder="Meta Description (SEO leírás - max 160 karakter)"
                      value={newClinic.meta_description || ''}
                      onChange={(e) => setNewClinic(prev => ({...prev, meta_description: e.target.value}))}
                      className="form-input h-20 w-full resize-none"
                      maxLength="160"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OpenGraph Kép (Social Media előnézet)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleClinicOgImageUpload(e.target.files[0])}
                        className="form-input"
                      />
                      {newClinic.og_image && (
                        <div className="mt-2">
                          <img 
                            src={`${backendUrl}${newClinic.og_image}`} 
                            alt="OG Preview" 
                            className="h-20 w-auto rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => setNewClinic(prev => ({...prev, og_image: ''}))}
                            className="text-red-600 text-sm mt-1 hover:underline"
                          >
                            Kép törlése
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* FogPortál aloldal beállítások */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <label className="flex items-center space-x-3 mb-3">
                      <input
                        type="checkbox"
                        checked={newClinic.has_subpage}
                        onChange={(e) => setNewClinic(prev => ({...prev, has_subpage: e.target.checked, subpage_url: e.target.checked ? prev.subpage_url : ''}))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Van FogPortálos aloldala</span>
                    </label>
                    
                    {newClinic.has_subpage && (
                      <input
                        type="url"
                        placeholder="FogPortál aloldal URL (pl. https://fogportal.hu/xyz-fogaszat)"
                        value={newClinic.subpage_url}
                        onChange={(e) => setNewClinic(prev => ({...prev, subpage_url: e.target.value}))}
                        className="form-input"
                        required
                      />
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Cím"
                    value={newClinic.address}
                    onChange={(e) => setNewClinic(prev => ({...prev, address: e.target.value}))}
                    className="form-input"
                    required
                  />
                  <input
                    type="date"
                    placeholder="Előfizetés kezdete"
                    value={newClinic.subscription_start}
                    onChange={(e) => setNewClinic(prev => ({...prev, subscription_start: e.target.value}))}
                    className="form-input"
                    required
                  />
                  <input
                    type="date"
                    placeholder="Előfizetés vége"
                    value={newClinic.subscription_end}
                    onChange={(e) => setNewClinic(prev => ({...prev, subscription_end: e.target.value}))}
                    className="form-input"
                    required
                  />
                </div>
                
                <textarea
                  placeholder="Nyitvatartás"
                  value={newClinic.opening_hours}
                  onChange={(e) => setNewClinic(prev => ({...prev, opening_hours: e.target.value}))}
                  className="form-input h-20"
                  required
                />
                
                <textarea
                  placeholder="Leírás"
                  value={newClinic.description}
                  onChange={(e) => setNewClinic(prev => ({...prev, description: e.target.value}))}
                  className="form-input h-24"
                  required
                />

                {/* Services Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Szolgáltatások</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {services.map(service => (
                      <label key={service} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newClinic.services.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                          className="mr-2"
                        />
                        <span className="text-sm">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logó</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files[0])}
                    className="form-input"
                  />
                </div>

                {/* Images Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Galéria képek</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                    className="form-input"
                  />
                </div>

                <button
                  type="submit"
                  className="primary-button"
                >
                  Klinika létrehozása
                </button>
              </form>
            </div>

            {/* Klinikák táblázat */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800">Meglévő klinikák ({clinicsArray.length} db)</h3>
              </div>

              {/* Tömeges műveletek */}
              {clinicsArray.length > 0 && (
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">
                        {selectedClinicIds.length} klinika kiválasztva
                      </span>
                      <button
                        onClick={handleSelectAll}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        {selectedClinicIds.length === paginatedClinics.length ? 'Mind törlése' : 'Mind kijelölése'}
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <select
                        value={bulkOperation}
                        onChange={(e) => setBulkOperation(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={selectedClinicIds.length === 0}
                      >
                        <option value="">Válasszon műveletet...</option>
                        <option value="delete">🗑️ Tömeges törlés</option>
                        <option value="archive">📦 Tömeges archiválás</option>
                        <option value="activate">✅ Tömeges aktiválás</option>
                        <option value="feature">⭐ Tömeges kiemelés</option>
                        <option value="unfeature">⭐ Kiemelt levétele</option>
                      </select>
                      
                      <button
                        onClick={handleBulkOperation}
                        disabled={selectedClinicIds.length === 0 || !bulkOperation}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Végrehajtás
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={paginatedClinics.length > 0 && selectedClinicIds.length === paginatedClinics.length}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Klinika név
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Város
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Szolgáltatások
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lejárati dátum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Státusz
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Műveletek
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clinicsArray.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                          Nincsenek klinikák
                        </td>
                      </tr>
                    ) : (
                      paginatedClinics.map(clinic => {
                        const daysUntilExpiry = getDaysUntilExpiry(clinic.expiry_date);
                        return (
                          <tr key={clinic.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedClinicIds.includes(clinic.id)}
                                onChange={() => handleSelectClinic(clinic.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{clinic.name}</div>
                                  <div className="text-sm text-gray-500">{clinic.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatCityDisplay(clinic.city, clinic.district)}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {clinic.services && clinic.services.length > 0 ? (
                                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                    {clinic.services.length} szolgáltatás
                                  </span>
                                ) : (
                                  'Nincs megadva'
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <div className="text-sm text-gray-900">{formatDate(clinic.expiry_date)}</div>
                                {daysUntilExpiry !== null && (
                                  <div className={`text-xs ${
                                    daysUntilExpiry <= 0 ? 'text-red-600 font-bold' : 
                                    daysUntilExpiry <= 7 ? 'text-red-500' :
                                    daysUntilExpiry <= 30 ? 'text-yellow-600' : 'text-green-600'
                                  }`}>
                                    {daysUntilExpiry <= 0 ? 'LEJÁRT!' : `${daysUntilExpiry} nap`}
                                  </div>
                                )}
                                <input
                                  type="date"
                                  className="mt-1 text-xs border rounded px-1"
                                  defaultValue={clinic.expiry_date}
                                  onBlur={(e) => {
                                    if (e.target.value !== clinic.expiry_date) {
                                      updateClinicExpiryDate(clinic.id, e.target.value);
                                    }
                                  }}
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  clinic.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {clinic.active ? 'Aktív' : 'Inaktív'}
                                </span>
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  clinic.featured ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {clinic.featured ? 'Kiemelt' : 'Normál'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex flex-col space-y-1">
                                {/* External Links */}
                                {clinic.website && (
                                  <button
                                    onClick={() => openClinicWebsite(clinic.website)}
                                    className="text-green-600 hover:text-green-900 text-xs bg-green-50 hover:bg-green-100 px-2 py-1 rounded border"
                                    title="Weboldal megnyitása"
                                  >
                                    🌐 Weboldal
                                  </button>
                                )}
                                <button
                                  onClick={() => openClinicProfile(clinic)}
                                  className="text-blue-600 hover:text-blue-900 text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded border"
                                  title="FogPortál profil megnyitása"
                                >
                                  📄 {clinic.has_subpage ? 'Teljes oldal' : 'Profil'}
                                </button>
                                
                                {/* Original Action Buttons */}
                                <button
                                  onClick={() => startEditClinic(clinic)}
                                  className="text-blue-600 hover:text-blue-900 text-xs"
                                >
                                  Szerkesztés
                                </button>
                                <button
                                  onClick={() => toggleActive(clinic.id, clinic.active)}
                                  className="text-yellow-600 hover:text-yellow-900 text-xs"
                                >
                                  {clinic.active ? 'Archiválás' : 'Aktiválás'}
                                </button>
                                <button
                                  onClick={() => toggleFeatured(clinic.id, clinic.featured)}
                                  className="text-purple-600 hover:text-purple-900 text-xs"
                                >
                                  {clinic.featured ? 'Kiemelt levétele' : 'Kiemelés'}
                                </button>
                                <button
                                  onClick={() => deleteClinicPermanently(clinic.id, clinic.name)}
                                  className="text-red-600 hover:text-red-900 text-xs font-semibold"
                                >
                                  Végleges törlés
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => setClinicsPage(Math.max(1, clinicsPage - 1))}
                      disabled={clinicsPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Előző
                    </button>
                    <span className="text-sm text-gray-700">
                      {clinicsPage}. oldal / {totalPages}
                    </span>
                    <button
                      onClick={() => setClinicsPage(Math.min(totalPages, clinicsPage + 1))}
                      disabled={clinicsPage === totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Következő
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">{startIndex + 1}</span>
                        -
                        <span className="font-medium">{Math.min(endIndex, clinicsArray.length)}</span>
                        {' '}klinika (összesen: <span className="font-medium">{clinicsArray.length}</span>)
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => setClinicsPage(Math.max(1, clinicsPage - 1))}
                          disabled={clinicsPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Előző</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                          let pageNum;
                          if (totalPages <= 7) {
                            pageNum = index + 1;
                          } else {
                            if (clinicsPage <= 4) {
                              pageNum = index + 1;
                            } else if (clinicsPage > totalPages - 4) {
                              pageNum = totalPages - 6 + index;
                            } else {
                              pageNum = clinicsPage - 3 + index;
                            }
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setClinicsPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                pageNum === clinicsPage
                                  ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => setClinicsPage(Math.min(totalPages, clinicsPage + 1))}
                          disabled={clinicsPage === totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Következő</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Edit Clinic Modal */}
            {editingClinic && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-gray-800">Klinika szerkesztése</h3>
                      <button
                        onClick={cancelEditClinic}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <form onSubmit={handleUpdateClinic} className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Klinika neve"
                        value={editClinic.name}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, name: e.target.value }))}
                        className="form-input"
                        required
                      />
                      <CityAutocomplete
                        value={editClinic.city}
                        onChange={(value) => {
                          const district = extractDistrictFromCity(value);
                          setEditClinic(prev => ({ 
                            ...prev, 
                            city: value,
                            district: district
                          }));
                        }}
                        placeholder="Város keresése..."
                        required={true}
                      />
                      <input
                        type="tel"
                        placeholder="Telefonszám"
                        value={editClinic.phone}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, phone: e.target.value }))}
                        className="form-input"
                        required
                      />
                      <input
                        type="email"
                        placeholder="Email cím"
                        value={editClinic.email}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, email: e.target.value }))}
                        className="form-input"
                        required
                      />
                      <input
                        type="url"
                        placeholder="Weboldal (opcionális)"
                        value={editClinic.website}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, website: e.target.value }))}
                        className="form-input"
                      />
                    </div>
                    
                    <textarea
                      placeholder="Cím"
                      value={editClinic.address}
                      onChange={(e) => setEditClinic(prev => ({ ...prev, address: e.target.value }))}
                      className="form-input w-full"
                      rows={2}
                      required
                    />

                    {/* Social Media Links Section */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-medium text-gray-800 mb-3">Social Media Linkek</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="url"
                          placeholder="Facebook URL (opcionális)"
                          value={editClinic.facebook_url}
                          onChange={(e) => setEditClinic(prev => ({ ...prev, facebook_url: e.target.value }))}
                          className="form-input"
                        />
                        <input
                          type="url"
                          placeholder="Instagram URL (opcionális)"
                          value={editClinic.instagram_url}
                          onChange={(e) => setEditClinic(prev => ({ ...prev, instagram_url: e.target.value }))}
                          className="form-input"
                        />
                        <input
                          type="url"
                          placeholder="LinkedIn URL (opcionális)"
                          value={editClinic.linkedin_url}
                          onChange={(e) => setEditClinic(prev => ({ ...prev, linkedin_url: e.target.value }))}
                          className="form-input"
                        />
                        <input
                          type="url"
                          placeholder="YouTube csatorna URL (opcionális)"
                          value={editClinic.youtube_url}
                          onChange={(e) => setEditClinic(prev => ({ ...prev, youtube_url: e.target.value }))}
                          className="form-input"
                        />
                      </div>
                    </div>

                    {/* Intro Video Section */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-medium text-gray-800 mb-3">📹 Bemutatkozó Videó</h4>
                      <input
                        type="url"
                        placeholder="YouTube videó URL (pl: https://youtu.be/6zHLt1q8h1Q)"
                        value={editClinic.intro_video_url}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, intro_video_url: e.target.value }))}
                        className="form-input"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        A bemutatkozó videó a klinika profil oldalon jelenik meg beágyazva.
                      </p>
                    </div>

                    {/* Foglaljorvost Link Section */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-medium text-gray-800 mb-3">⏰ Online Időpontfoglalás</h4>
                      <div className="bg-green-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-green-800 mb-2">
                          🌐 <strong>Foglaljorvost.hu integráció:</strong> Adja meg a Foglaljorvost.hu profil linkjét
                        </p>
                        <p className="text-xs text-green-600">
                          Ha van Foglaljorvost.hu profilja, a páciensek közvetlenül online foglalhatnak időpontot.
                        </p>
                      </div>
                      <input
                        type="url"
                        placeholder="Foglaljorvost.hu profil link (pl: https://foglaljorvost.hu/orvos/...)"
                        value={editClinic.foglaljorvost_link || ''}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, foglaljorvost_link: e.target.value }))}
                        className="form-input w-full"
                      />
                    </div>

                    {/* Google Business Link Section */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-medium text-gray-800 mb-3">🌟 Google Értékelések</h4>
                      <div className="bg-blue-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-blue-800 mb-2">
                          📍 <strong>Google Business profilhoz csatlakoztatás:</strong> Adja meg a rövidített Google Maps linket (pl. https://g.co/kgs/RyzNpd6)
                        </p>
                        <p className="text-xs text-blue-600">
                          A rendszer automatikusan lekéri és megjeleníti a Google értékeléseket a klinikájáról.
                        </p>
                      </div>
                      <input
                        type="url"
                        placeholder="Google Business Link (pl. https://g.co/kgs/RyzNpd6)"
                        value={editClinic.google_business_link || ''}
                        onChange={(e) => setEditClinic(prev => ({ ...prev, google_business_link: e.target.value }))}
                        className="form-input w-full"
                      />
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Szolgáltatások</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                        {services.map(service => (
                          <label key={service} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editClinic.services.includes(service)}
                              onChange={() => handleEditServiceToggle(service)}
                              className="mr-2"
                            />
                            <span className="text-sm">{service}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <textarea
                      placeholder="Nyitvatartás"
                      value={editClinic.opening_hours}
                      onChange={(e) => setEditClinic(prev => ({ ...prev, opening_hours: e.target.value }))}
                      className="form-input w-full"
                      rows={3}
                      required
                    />

                    <textarea
                      placeholder="Leírás"
                      value={editClinic.description}
                      onChange={(e) => setEditClinic(prev => ({ ...prev, description: e.target.value }))}
                      className="form-input w-full"
                      rows={4}
                      required
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Lejárati dátum
                        </label>
                        <input
                          type="date"
                          value={editClinic.expiry_date || ''}
                          onChange={(e) => setEditClinic(prev => ({ ...prev, expiry_date: e.target.value }))}
                          className="form-input"
                        />
                      </div>
                    </div>

                    {/* SEO Beállítások szerkesztéshez */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-medium text-gray-800 mb-3">🔍 SEO Beállítások</h4>
                      <div className="bg-purple-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-purple-800 mb-2">
                          📈 <strong>SEO optimalizálás:</strong> Ezek a mezők javítják a Google találati helyezést és a social media megjelenést
                        </p>
                        <p className="text-xs text-purple-600">
                          Ha üresen hagyja, a rendszer automatikusan generálja a klinika neve és leírása alapján.
                        </p>
                      </div>
                      <div className="space-y-4">
                        <input
                          type="text"
                          placeholder="Meta Title (SEO cím - max 60 karakter)"
                          value={editClinic.meta_title || ''}
                          onChange={(e) => setEditClinic(prev => ({...prev, meta_title: e.target.value}))}
                          className="form-input w-full"
                          maxLength="60"
                        />
                        <textarea
                          placeholder="Meta Description (SEO leírás - max 160 karakter)"
                          value={editClinic.meta_description || ''}
                          onChange={(e) => setEditClinic(prev => ({...prev, meta_description: e.target.value}))}
                          className="form-input h-20 w-full resize-none"
                          maxLength="160"
                        />
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            OpenGraph Kép (Social Media előnézet)
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleEditClinicOgImageUpload(e.target.files[0])}
                            className="form-input"
                          />
                          {editClinic.og_image && (
                            <div className="mt-2">
                              <img 
                                src={`${backendUrl}${editClinic.og_image}`} 
                                alt="OG Preview" 
                                className="h-20 w-auto rounded border"
                              />
                              <button
                                type="button"
                                onClick={() => setEditClinic(prev => ({...prev, og_image: ''}))}
                                className="text-red-600 text-sm mt-1 hover:underline"
                              >
                                Kép törlése
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Logo és Galéria képek feltöltése szerkesztéshez */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-medium text-gray-800 mb-3">🖼️ Klinika Képek</h4>
                      <div className="space-y-4">
                        {/* Logo feltöltés */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logó kezelése
                          </label>
                          
                          {/* Current logo with delete option */}
                          {editClinic.logo && !deleteCurrentLogo && !editLogoFile && (
                            <div className="mt-2 mb-3">
                              <p className="text-sm text-gray-600 mb-2">Jelenlegi logó:</p>
                              <div className="flex items-start space-x-3">
                                <img 
                                  src={`${backendUrl}${editClinic.logo}`} 
                                  alt="Current Logo" 
                                  className="h-20 w-auto rounded border"
                                />
                                <button
                                  type="button"
                                  onClick={handleRemoveLogo}
                                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                                >
                                  🗑️ Logó törlése
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {deleteCurrentLogo && !editLogoFile && (
                            <p className="text-sm text-orange-600 mb-2">
                              ⚠️ A logó törölve lesz mentéskor
                            </p>
                          )}
                          
                          {/* Upload new logo */}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              setEditLogoFile(e.target.files[0]);
                              setDeleteCurrentLogo(false); // Cancel delete if uploading new
                            }}
                            className="form-input"
                          />
                          {editLogoFile && (
                            <p className="text-sm text-green-600 mt-1">
                              ✅ Új logó kiválasztva: {editLogoFile.name}
                            </p>
                          )}
                        </div>

                        {/* Galéria képek feltöltése */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Galéria képek kezelése
                          </label>
                          
                          {/* Current gallery images with delete buttons */}
                          {editClinic.images && editClinic.images.length > 0 && (
                            <div className="mt-2 mb-3">
                              <p className="text-sm text-gray-600 mb-2">Jelenlegi galéria képek ({editClinic.images.length} db):</p>
                              <div className="grid grid-cols-3 gap-3">
                                {editClinic.images.map((img, idx) => (
                                  <div key={idx} className="relative group">
                                    <img 
                                      src={`${backendUrl}${img}`} 
                                      alt={`Gallery ${idx + 1}`} 
                                      className="w-full h-24 object-cover rounded border"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveGalleryImage(img)}
                                      className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded text-xs hover:bg-red-700 transition-colors opacity-90"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {editClinic.images && editClinic.images.length === 0 && (
                            <p className="text-sm text-gray-500 mb-2">
                              Nincs galéria kép
                            </p>
                          )}
                          
                          {/* Upload new images */}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setEditSelectedFiles(Array.from(e.target.files))}
                            className="form-input"
                          />
                          {editSelectedFiles.length > 0 && (
                            <p className="text-sm text-green-600 mt-1">
                              ✅ {editSelectedFiles.length} új kép kiválasztva
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-4 pt-4">
                      <button
                        type="submit"
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Frissítés
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditClinic}
                        className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        Mégse
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bulk Operations Confirmation Modal */}
        {showBulkConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Megerősítés szükséges
              </h3>
              
              <p className="text-gray-600 mb-6">
                {getBulkOperationMessage()}
                {bulkOperation === 'delete' && (
                  <span className="block mt-2 text-red-600 font-medium">
                    ⚠️ Ez a művelet nem vonható vissza!
                  </span>
                )}
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelBulkOperation}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={bulkOperationInProgress}
                >
                  Mégse
                </button>
                <button
                  onClick={executeBulkOperation}
                  disabled={bulkOperationInProgress}
                  className={`px-4 py-2 text-white rounded-md ${
                    bulkOperation === 'delete' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {bulkOperationInProgress ? 'Végrehajtás...' : 'Igen, végrehajtás'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Blog Management */}
        {adminView === 'blog' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800">Blog kezelése</h2>
              <div className="flex space-x-4">
                <button 
                  onClick={() => setAdminView('blog-new')}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  + Új bejegyzés
                </button>
                <button 
                  onClick={() => setAdminView('blog-categories')}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  Kategóriák
                </button>
              </div>
            </div>

            {/* Blog Posts List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Blogbejegyzések</h3>
                  <div className="flex space-x-2">
                    <select className="form-select text-sm">
                      <option value="">Összes státusz</option>
                      <option value="published">Publikált</option>
                      <option value="draft">Tervezet</option>
                      <option value="scheduled">Ütemezett</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cím
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Szerző
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kategóriák
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Státusz
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dátum
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Műveletek
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allBlogPosts.map(post => (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {post.featured_image && (
                              <img 
                                src={`${backendUrl}${post.featured_image}`} 
                                alt={post.title}
                                className="w-10 h-10 rounded object-cover mr-4"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">{post.title}</div>
                              <div className="text-sm text-gray-500 line-clamp-1">{post.excerpt}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {post.author}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {post.categories?.map((category, index) => (
                              <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {category}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            post.status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : post.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {post.status === 'published' ? 'Publikált' : 
                             post.status === 'draft' ? 'Tervezet' : 'Ütemezett'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {post.published_at ? new Date(post.published_at).toLocaleDateString('hu-HU') : 'Nincs'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => startEditPost(post)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Szerkesztés
                            </button>
                            <button 
                              onClick={() => handleDeleteBlogPost(post.id, post.title)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Törlés
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Blog Categories Management */}
        {adminView === 'blog-categories' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800">Blog Kategóriák kezelése</h2>
              <button 
                onClick={() => setAdminView('blog')}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                ← Vissza a Blog kezeléshez
              </button>
            </div>
            
            {/* Create Category Form */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Új kategória létrehozása</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategória neve *
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="pl. Fogápolás, Implantátum, Esztétikai fogászat"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leírás (opcionális)
                  </label>
                  <input
                    type="text"
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    placeholder="Rövid leírás a kategóriáról"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6">
                <button 
                  onClick={createBlogCategory}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  + Kategória létrehozása
                </button>
              </div>
            </div>

            {/* Categories List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">
                  Meglévő kategóriák ({blogCategories.length})
                </h3>
              </div>
              
              {blogCategories.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <p className="mb-4">Még nincsenek kategóriák létrehozva.</p>
                  <p className="text-sm">Hozza létre az első kategóriát a fenti form segítségével!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Név
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Slug
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Leírás
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Létrehozva
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Műveletek
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {blogCategories.map(category => (
                        <tr key={category.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{category.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{category.slug}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {category.description || '—'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {category.created_at ? new Date(category.created_at).toLocaleDateString('hu-HU') : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                if (window.confirm(`Biztosan törölni szeretné a "${category.name}" kategóriát?`)) {
                                  deleteBlogCategory(category.id, category.name);
                                }
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              Törlés
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* New Blog Post */}
        {adminView === 'blog-new' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800">Új blogbejegyzés</h2>
              <button 
                onClick={() => setAdminView('blog')}
                className="text-gray-600 hover:text-gray-800"
              >
                ← Vissza a listához
              </button>
            </div>

            <form onSubmit={handleCreateBlogPost} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Cím"
                      value={newBlogPost.title}
                      onChange={(e) => setNewBlogPost(prev => ({...prev, title: e.target.value}))}
                      className="form-input text-xl font-bold"
                      required
                    />
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tartalom</label>
                      <QuillEditor
                        value={newBlogPost.content}
                        onChange={(content) => setNewBlogPost(prev => ({...prev, content}))}
                        placeholder="Írja ide a blog bejegyzés tartalmát..."
                      />
                    </div>

                    <textarea
                      placeholder="Rövid leírás (excerpt)"
                      value={newBlogPost.excerpt}
                      onChange={(e) => setNewBlogPost(prev => ({...prev, excerpt: e.target.value}))}
                      className="form-input h-24"
                      required
                    />
                  </div>
                </div>

                {/* SEO Section */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">SEO beállítások</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Meta title (ha üres, a cím lesz használva)"
                      value={newBlogPost.meta_title}
                      onChange={(e) => setNewBlogPost(prev => ({...prev, meta_title: e.target.value}))}
                      className="form-input"
                    />
                    <textarea
                      placeholder="Meta description (ha üres, az excerpt lesz használva)"
                      value={newBlogPost.meta_description}
                      onChange={(e) => setNewBlogPost(prev => ({...prev, meta_description: e.target.value}))}
                      className="form-input h-20"
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Publish Box */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Publikálás</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Szerző"
                      value={newBlogPost.author}
                      onChange={(e) => setNewBlogPost(prev => ({...prev, author: e.target.value}))}
                      className="form-input"
                      required
                    />
                    
                    <select
                      value={newBlogPost.status}
                      onChange={(e) => setNewBlogPost(prev => ({...prev, status: e.target.value}))}
                      className="form-select"
                    >
                      <option value="draft">Tervezet</option>
                      <option value="published">Publikálás</option>
                      <option value="scheduled">Ütemezés</option>
                    </select>

                    {newBlogPost.status === 'scheduled' && (
                      <input
                        type="datetime-local"
                        value={newBlogPost.published_at}
                        onChange={(e) => setNewBlogPost(prev => ({...prev, published_at: e.target.value}))}
                        className="form-input"
                      />
                    )}

                    <button
                      type="submit"
                      className="w-full primary-button"
                    >
                      {newBlogPost.status === 'published' ? 'Publikálás' : 
                       newBlogPost.status === 'scheduled' ? 'Ütemezés' : 'Mentés tervezetként'}
                    </button>
                  </div>
                </div>

                {/* Featured Image */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Kiemelt kép</h3>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFeaturedImageFile(e.target.files[0])}
                    className="form-input"
                  />
                  {featuredImageFile && (
                    <div className="mt-4">
                      <img 
                        src={URL.createObjectURL(featuredImageFile)}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  )}
                </div>

                {/* Categories */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Kategóriák</h3>
                  
                  {/* Quick add category */}
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Új kategória hozzáadása
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Kategória neve (pl. Fogápolás, Implantátum)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const categoryName = e.target.value.trim();
                            if (categoryName) {
                              addQuickCategory(categoryName, true);
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.querySelector('input[placeholder*="Kategória neve"]');
                          const categoryName = input?.value?.trim();
                          if (categoryName) {
                            addQuickCategory(categoryName, true);
                            input.value = '';
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        + Hozzáadás
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter lenyomásával vagy a "Hozzáadás" gombbal létrehozhat új kategóriát
                    </p>
                  </div>

                  {/* Existing categories */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Válasszon meglévő kategóriákból:</h4>
                    {blogCategories.length > 0 ? (
                      blogCategories.map(category => (
                        <label key={category.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newBlogPost.categories.includes(category.name)}
                            onChange={() => toggleCategoryForPost(category.name, true)}
                            className="mr-2"
                          />
                          <span className="text-sm">{category.name}</span>
                          {category.description && (
                            <span className="text-xs text-gray-500 ml-2">({category.description})</span>
                          )}
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        Még nincsenek kategóriák. Hozza létre az első kategóriát fent!
                      </p>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Címkék</h3>
                  <input
                    type="text"
                    placeholder="Új címke hozzáadása"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const tag = e.target.value.trim();
                        if (tag) {
                          addTagToPost(tag, true);
                          e.target.value = '';
                        }
                      }
                    }}
                    className="form-input mb-3"
                  />
                  <div className="flex flex-wrap gap-2">
                    {newBlogPost.tags.map((tag, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTagFromPost(tag, true)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Edit Blog Post Modal */}
        {editingPost && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-8 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Blogbejegyzés szerkesztése</h3>
                <button 
                  onClick={cancelEditPost}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleUpdateBlogPost} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Cím"
                      value={editBlogPost.title}
                      onChange={(e) => setEditBlogPost(prev => ({...prev, title: e.target.value}))}
                      className="form-input text-xl font-bold"
                      required
                    />
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tartalom</label>
                      <QuillEditor
                        value={editBlogPost.content}
                        onChange={(content) => setEditBlogPost(prev => ({...prev, content}))}
                        placeholder="Szerkessze a blog bejegyzés tartalmát..."
                      />
                    </div>

                    <textarea
                      placeholder="Rövid leírás (excerpt)"
                      value={editBlogPost.excerpt}
                      onChange={(e) => setEditBlogPost(prev => ({...prev, excerpt: e.target.value}))}
                      className="form-input h-24"
                      required
                    />
                  </div>

                  {/* SEO Section */}
                  <div className="border-t pt-6">
                    <h4 className="text-lg font-semibold mb-4">SEO beállítások</h4>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Meta title"
                        value={editBlogPost.meta_title}
                        onChange={(e) => setEditBlogPost(prev => ({...prev, meta_title: e.target.value}))}
                        className="form-input"
                      />
                      <textarea
                        placeholder="Meta description"
                        value={editBlogPost.meta_description}
                        onChange={(e) => setEditBlogPost(prev => ({...prev, meta_description: e.target.value}))}
                        className="form-input h-20"
                      />
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Publish Box */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-4">Publikálás</h4>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Szerző"
                        value={editBlogPost.author}
                        onChange={(e) => setEditBlogPost(prev => ({...prev, author: e.target.value}))}
                        className="form-input"
                        required
                      />
                      
                      <select
                        value={editBlogPost.status}
                        onChange={(e) => setEditBlogPost(prev => ({...prev, status: e.target.value}))}
                        className="form-select"
                      >
                        <option value="draft">Tervezet</option>
                        <option value="published">Publikálás</option>
                        <option value="scheduled">Ütemezés</option>
                      </select>

                      {editBlogPost.status === 'scheduled' && (
                        <input
                          type="datetime-local"
                          value={editBlogPost.published_at}
                          onChange={(e) => setEditBlogPost(prev => ({...prev, published_at: e.target.value}))}
                          className="form-input"
                        />
                      )}

                      <div className="flex space-x-2">
                        <button
                          type="submit"
                          className="flex-1 primary-button"
                        >
                          Frissítés
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditPost}
                          className="flex-1 secondary-button"
                        >
                          Mégse
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Categories */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-4">Kategóriák</h4>
                    
                    {/* Quick add category */}
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Új kategória hozzáadása
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Kategória neve"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const categoryName = e.target.value.trim();
                              if (categoryName) {
                                addQuickCategory(categoryName, false);
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.querySelector('.border.rounded-lg input[placeholder="Kategória neve"]');
                            const categoryName = input?.value?.trim();
                            if (categoryName) {
                              addQuickCategory(categoryName, false);
                              input.value = '';
                            }
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs"
                        >
                          + Hozzá
                        </button>
                      </div>
                    </div>

                    {/* Existing categories */}
                    <div className="space-y-2">
                      {blogCategories.length > 0 ? (
                        blogCategories.map(category => (
                          <label key={category.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editBlogPost.categories.includes(category.name)}
                              onChange={() => toggleCategoryForPost(category.name, false)}
                              className="mr-2"
                            />
                            <span className="text-sm">{category.name}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          Még nincsenek kategóriák.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-4">Címkék</h4>
                    <input
                      type="text"
                      placeholder="Új címke hozzáadása"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const tag = e.target.value.trim();
                          if (tag) {
                            addTagToPost(tag, false);
                            e.target.value = '';
                          }
                        }
                      }}
                      className="form-input mb-3"
                    />
                    <div className="flex flex-wrap gap-2">
                      {editBlogPost.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTagFromPost(tag, false)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
        {adminView === 'partners' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Partnerek kezelése</h2>
            
            {/* Create/Edit Partner Form */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                {editingPartner ? 'Partner szerkesztése' : 'Új partner hozzáadása'}
              </h3>
              <form 
                key={editingPartner ? editingPartner.id : 'new'}
                onSubmit={editingPartner ? handleUpdatePartner : handleCreatePartner} 
                className="space-y-4"
              >
                <input
                  name="name"
                  type="text"
                  placeholder="Partner neve"
                  className="form-input"
                  value={partnerFormData.name}
                  onChange={(e) => setPartnerFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
                <input
                  name="website"
                  type="url"
                  placeholder="Weboldal (opcionális)"
                  className="form-input"
                  value={partnerFormData.website}
                  onChange={(e) => setPartnerFormData(prev => ({ ...prev, website: e.target.value }))}
                />
                
                {/* Social Media Links Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">Social Media Linkek</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      name="facebook_url"
                      type="url"
                      placeholder="Facebook URL (opcionális)"
                      className="form-input"
                      value={partnerFormData.facebook_url}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, facebook_url: e.target.value }))}
                    />
                    <input
                      name="instagram_url"
                      type="url"
                      placeholder="Instagram URL (opcionális)"
                      className="form-input"
                      value={partnerFormData.instagram_url}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, instagram_url: e.target.value }))}
                    />
                    <input
                      name="linkedin_url"
                      type="url"
                      placeholder="LinkedIn URL (opcionális)"
                      className="form-input"
                      value={partnerFormData.linkedin_url}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    />
                  </div>
                </div>
                <input
                  name="order"
                  type="number"
                  placeholder="Sorrend"
                  className="form-input"
                  value={partnerFormData.order}
                  onChange={(e) => setPartnerFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 1 }))}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                  <input
                    name="logo"
                    type="file"
                    accept="image/*"
                    className="form-input"
                    required={!editingPartner}
                  />
                  {editingPartner && editingPartner.logo && (
                    <div className="mt-2 flex items-center space-x-2">
                      <img 
                        src={`${backendUrl}${editingPartner.logo}`} 
                        alt={editingPartner.name}
                        className="h-12 w-12 object-contain border rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{display: 'none'}} className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs">
                        Nincs kép
                      </div>
                      <span className="text-sm text-gray-600">Jelenlegi logo (új fájl választása esetén lecserélődik)</span>
                    </div>
                  )}
                </div>
                <div className="flex space-x-4">
                  <button type="submit" className="primary-button">
                    {editingPartner ? 'Partner frissítése' : 'Partner hozzáadása'}
                  </button>
                  {editingPartner && (
                    <button 
                      type="button" 
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Mégse
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Partners List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Partnerek ({allPartners.length})</h3>
                <div className="text-sm text-gray-600">
                  Aktív: {allPartners.filter(p => p.active !== false).length} | 
                  Inaktív: {allPartners.filter(p => p.active === false).length}
                </div>
              </div>
              
              {allPartners.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  Még nincsenek partnerek hozzáadva.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Név</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weboldal</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sorrend</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állapot</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allPartners.map(partner => (
                        <tr key={partner.id} className={partner.active === false ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <img 
                              src={`${backendUrl}${partner.logo}`} 
                              alt={partner.name}
                              className="h-12 w-12 object-contain border rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div style={{display: 'none'}} className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs">
                              Nincs kép
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{partner.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {partner.website ? (
                              <a 
                                href={partner.website} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Weboldal →
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {partner.order}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              partner.active !== false 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {partner.active !== false ? 'Aktív' : 'Inaktív'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleEditPartner(partner)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Szerkesztés
                            </button>
                            <button
                              onClick={() => togglePartnerActive(partner.id)}
                              className={`${
                                partner.active !== false 
                                  ? 'text-orange-600 hover:text-orange-800' 
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                            >
                              {partner.active !== false ? 'Inaktiválás' : 'Aktiválás'}
                            </button>
                            <button
                              onClick={() => deletePartner(partner.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Törlés
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Widget Generator View */}
        {adminView === 'widget' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Partner Widget Generátor</h2>
            
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Widget Beállítások</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Size Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Widget Méret
                  </label>
                  <select
                    value={widgetSize}
                    onChange={(e) => setWidgetSize(e.target.value)}
                    className="form-select w-full"
                  >
                    <option value="small">Kicsi (120x40px)</option>
                    <option value="medium">Közepes (180x60px)</option>
                    <option value="large">Nagy (240x80px)</option>
                    <option value="xl">Extra Nagy (300x100px)</option>
                  </select>
                </div>

                {/* Style Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Widget Stílus
                  </label>
                  <select
                    value={widgetStyle}
                    onChange={(e) => setWidgetStyle(e.target.value)}
                    className="form-select w-full"
                  >
                    <option value="light">Világos</option>
                    <option value="dark">Sötét</option>
                  </select>
                </div>
              </div>

              <button
                onClick={generateWidgetCode}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Widget Kód Generálása
              </button>
            </div>

            {/* Generated Widget Codes */}
            {widgetEmbedCode && (
              <div className="space-y-6">
                {/* Live Preview */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Élő Előnézet</h3>
                  <div className="p-4 bg-gray-50 rounded border flex items-center justify-center">
                    {(() => {
                      const sizeConfigs = {
                        small: { width: '120px', height: '40px', logoHeight: '24px', fontSize: '12px', padding: '8px 12px' },
                        medium: { width: '180px', height: '60px', logoHeight: '32px', fontSize: '14px', padding: '12px 16px' },
                        large: { width: '240px', height: '80px', logoHeight: '40px', fontSize: '16px', padding: '16px 20px' },
                        xl: { width: '300px', height: '100px', logoHeight: '48px', fontSize: '18px', padding: '20px 24px' }
                      };
                      
                      const config = sizeConfigs[widgetSize] || sizeConfigs.medium;
                      const isDark = widgetStyle === 'dark';
                      
                      const widgetStyle_computed = {
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: config.width,
                        height: config.height,
                        backgroundColor: isDark ? '#1f2937' : '#ffffff',
                        border: `2px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        padding: config.padding,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        fontSize: config.fontSize,
                        fontWeight: '600',
                        color: isDark ? '#ffffff' : '#1f2937',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        position: 'relative',
                        overflow: 'hidden'
                      };
                      
                      return (
                        <div 
                          style={widgetStyle_computed}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            e.target.style.borderColor = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            e.target.style.borderColor = isDark ? '#374151' : '#e5e7eb';
                          }}
                          onClick={() => alert('Widget előnézet kattintás! (Éles környezetben új ablakban nyitná meg a FogPortál.hu-t)')}
                        >
                          {/* Fog ikon SVG */}
                          <svg 
                            width={config.logoHeight} 
                            height={config.logoHeight} 
                            viewBox="0 0 512 512" 
                            style={{marginRight: '8px', flexShrink: 0}}
                          >
                            <defs>
                              <linearGradient id={`fogGradient-${widgetSize}-${widgetStyle}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{stopColor: '#60a5fa', stopOpacity: 1}} />
                                <stop offset="100%" style={{stopColor: '#3b82f6', stopOpacity: 1}} />
                              </linearGradient>
                            </defs>
                            <path 
                              d="M256 80c-53 0-96 43-96 96v80c0 35 18 67 48 85v75c0 26 22 48 48 48s48-22 48-48v-75c30-18 48-50 48-85v-80c0-53-43-96-96-96zm-32 96c0-18 14-32 32-32s32 14 32 32v32c0 18-14 32-32 32s-32-14-32-32v-32z" 
                              fill={isDark ? '#ffffff' : `url(#fogGradient-${widgetSize}-${widgetStyle})`}
                            />
                            <path 
                              d="M224 200c0-18 14-32 32-32s32 14 32 32v32c0 18-14 32-32 32s-32-14-32-32v-32z" 
                              fill={isDark ? '#1f2937' : '#ffffff'}
                            />
                            <circle cx="240" cy="190" r="4" fill={isDark ? '#ffffff' : '#3b82f6'} />
                            <circle cx="272" cy="190" r="4" fill={isDark ? '#ffffff' : '#3b82f6'} />
                          </svg>
                          
                          {/* Szöveg */}
                          <span style={{
                            flex: 1,
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            FogPortál.hu
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <p className="text-sm text-gray-600 mt-2 italic">
                    💡 Kattints a widget-re az interaktivitás teszteléséhez
                  </p>
                </div>

                {/* Embed Code */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Beágyazható Kód (Automatikus)</h3>
                    <button
                      onClick={() => copyToClipboard(widgetEmbedCode)}
                      className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      Másolás
                    </button>
                  </div>
                  <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto border">
                    <code>{widgetEmbedCode}</code>
                  </pre>
                </div>

                {/* Manual Code */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Beágyazható Kód (Manuális)</h3>
                    <button
                      onClick={() => copyToClipboard(widgetManualCode)}
                      className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      Másolás
                    </button>
                  </div>
                  <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto border">
                    <code>{widgetManualCode}</code>
                  </pre>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4">Használati Utasítás</h3>
                  <div className="text-blue-700 space-y-2">
                    <p><strong>1.</strong> Válassza ki a kívánt méretet és stílust</p>
                    <p><strong>2.</strong> Kattintson a "Widget Kód Generálása" gombra</p>
                    <p><strong>3.</strong> Másolja ki a generált kódot (automatikus vagy manuális verzió)</p>
                    <p><strong>4.</strong> Illessze be a kódot a partner weboldalába oda, ahol meg szeretné jeleníteni a widget-et</p>
                    <p><strong>5.</strong> A widget automatikusan megjelenik és kattintásra új ablakban megnyitja a FogPortál.hu főoldalát</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Data Migration View */}
        {adminView === 'migration' && (
          <DataMigrationView />
        )}

        {/* CRM View */}
        {adminView === 'crm' && (
          <CRMView adminToken={adminToken} backendUrl={backendUrl} />
        )}

        {/* Settings View */}
        {adminView === 'settings' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Beállítások</h2>
            
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Google Analytics 4 (GA4)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GA4 Tracking ID (pl.: G-XXXXXXXXXX)
                  </label>
                  <input
                    type="text"
                    value={settings.ga4_tracking_id}
                    onChange={(e) => setSettings(prev => ({...prev, ga4_tracking_id: e.target.value}))}
                    placeholder="G-XXXXXXXXXX"
                    className="form-input"
                    disabled={settingsLoading}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    A GA4 Property beállításaiban található Tracking ID. Ez automatikusan beállítja a Google Analytics követést minden oldalon.
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={saveSettings}
                    disabled={settingsLoading}
                    className="primary-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {settingsLoading ? 'Mentés...' : 'Beállítások mentése'}
                  </button>
                  
                  {settingsSaved && (
                    <div className="flex items-center text-green-600">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Mentve!
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Testimonials Management */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Vélemények kezelése</h3>
              
              {/* Add New Testimonial Form */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Új vélemény hozzáadása</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pontszám (1-5 csillag) *
                    </label>
                    <select
                      value={newTestimonial.rating}
                      onChange={(e) => setNewTestimonial(prev => ({...prev, rating: parseInt(e.target.value)}))}
                      className="form-input"
                    >
                      <option value={1}>⭐ 1 csillag</option>
                      <option value={2}>⭐⭐ 2 csillag</option>
                      <option value={3}>⭐⭐⭐ 3 csillag</option>
                      <option value={4}>⭐⭐⭐⭐ 4 csillag</option>
                      <option value={5}>⭐⭐⭐⭐⭐ 5 csillag</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Név *
                    </label>
                    <input
                      type="text"
                      value={newTestimonial.name}
                      onChange={(e) => setNewTestimonial(prev => ({...prev, name: e.target.value}))}
                      placeholder="Dr. Kovács Péter"
                      className="form-input"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Klinika neve *
                  </label>
                  <input
                    type="text"
                    value={newTestimonial.clinic_name}
                    onChange={(e) => setNewTestimonial(prev => ({...prev, clinic_name: e.target.value}))}
                    placeholder="Smile Fogászati Központ"
                    className="form-input"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vélemény szövege *
                  </label>
                  <textarea
                    value={newTestimonial.content}
                    onChange={(e) => setNewTestimonial(prev => ({...prev, content: e.target.value}))}
                    placeholder="A FogPortál.hu segítségével jelentősen nőtt a klinikám látogatottsága..."
                    rows={4}
                    className="form-input"
                  />
                </div>
                
                <button
                  onClick={createTestimonial}
                  className="primary-button"
                >
                  Vélemény hozzáadása
                </button>
              </div>
              
              {/* Existing Testimonials List */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Jelenlegi vélemények ({testimonials.length})</h4>
                
                {testimonialsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Vélemények betöltése...</p>
                  </div>
                ) : testimonials.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Még nincsenek vélemények. Adja hozzá az elsőt!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {testimonials.map(testimonial => (
                      <div key={testimonial.id} className="border rounded-lg p-4 bg-gray-50">
                        {editingTestimonial?.id === testimonial.id ? (
                          // Edit mode
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pontszám</label>
                                <select
                                  value={editingTestimonial.rating}
                                  onChange={(e) => setEditingTestimonial(prev => ({...prev, rating: parseInt(e.target.value)}))}
                                  className="form-input text-sm"
                                >
                                  <option value={1}>⭐ 1 csillag</option>
                                  <option value={2}>⭐⭐ 2 csillag</option>
                                  <option value={3}>⭐⭐⭐ 3 csillag</option>
                                  <option value={4}>⭐⭐⭐⭐ 4 csillag</option>
                                  <option value={5}>⭐⭐⭐⭐⭐ 5 csillag</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Név</label>
                                <input
                                  type="text"
                                  value={editingTestimonial.name}
                                  onChange={(e) => setEditingTestimonial(prev => ({...prev, name: e.target.value}))}
                                  className="form-input text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Klinika neve</label>
                              <input
                                type="text"
                                value={editingTestimonial.clinic_name}
                                onChange={(e) => setEditingTestimonial(prev => ({...prev, clinic_name: e.target.value}))}
                                className="form-input text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Vélemény</label>
                              <textarea
                                value={editingTestimonial.content}
                                onChange={(e) => setEditingTestimonial(prev => ({...prev, content: e.target.value}))}
                                rows={3}
                                className="form-input text-sm"
                              />
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateTestimonial(testimonial.id, editingTestimonial)}
                                className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                              >
                                Mentés
                              </button>
                              <button
                                onClick={() => setEditingTestimonial(null)}
                                className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600"
                              >
                                Mégse
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-4">
                                {testimonial.image ? (
                                  <img 
                                    src={`${backendUrl}${testimonial.image}`} 
                                    alt={testimonial.name}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-green-600 flex items-center justify-center text-white font-semibold">
                                    {testimonial.name.charAt(0)}
                                  </div>
                                )}
                                <div>
                                  <h5 className="font-semibold text-gray-900">{testimonial.name}</h5>
                                  <p className="text-sm text-gray-600">{testimonial.clinic_name}</p>
                                  <div className="flex items-center mt-1">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                      <span key={i} className="text-yellow-400">⭐</span>
                                    ))}
                                    <span className="text-sm text-gray-500 ml-2">({testimonial.rating}/5)</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setEditingTestimonial(testimonial)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  Szerkesztés
                                </button>
                                <button
                                  onClick={() => deleteTestimonial(testimonial.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Törlés
                                </button>
                              </div>
                            </div>
                            
                            <p className="text-gray-700 text-sm leading-relaxed mb-3">
                              "{testimonial.content}"
                            </p>
                            
                            {/* Image upload section */}
                            <div className="border-t pt-3">
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Kép feltöltése (opcionális, max 5MB)
                              </label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={(e) => handleTestimonialImageUpload(testimonial.id, e)}
                                  disabled={testimonialImageUploading}
                                  className="block text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                                {testimonialImageUploading && (
                                  <div className="flex items-center text-blue-600">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                                    <span className="text-xs">Feltöltés...</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* OpenGraph Settings */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">OpenGraph (OG) Meta Tagek</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alapértelmezett OpenGraph Kép
                  </label>
                  
                  {/* Current OG Image Display */}
                  {settings.default_og_image && (
                    <div className="mb-4">
                      <img 
                        src={`${backendUrl}${settings.default_og_image}`} 
                        alt="Alapértelmezett OG kép" 
                        className="w-64 h-32 object-cover rounded border"
                      />
                      <p className="text-sm text-gray-500 mt-1">Jelenlegi alapértelmezett OG kép</p>
                    </div>
                  )}
                  
                  {/* File Upload */}
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleOgImageUpload}
                      disabled={ogImageUploading}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {ogImageUploading && (
                      <div className="flex items-center text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Feltöltés...
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-2">
                    Ez a kép fog megjelenni a Facebook-on, Twitter-en és más közösségi oldalakon, amikor valaki megosztja az oldalakat. 
                    Támogatott formátumok: JPEG, PNG, GIF, WebP. Maximum fájlméret: 5MB.
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={saveSettings}
                    disabled={settingsLoading}
                    className="primary-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {settingsLoading ? 'Mentés...' : 'Beállítások mentése'}
                  </button>
                  
                  {settingsSaved && (
                    <div className="flex items-center text-green-600">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Mentve!
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Visitor Statistics Settings */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Látogatottság Statisztikák</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statisztika Screenshot
                  </label>
                  
                  {/* Current Visitor Stats Image Display */}
                  {settings.visitor_stats_image && (
                    <div className="mb-4">
                      <img 
                        src={`${backendUrl}${settings.visitor_stats_image}`} 
                        alt="Látogatottság statisztika" 
                        className="w-96 h-48 object-cover rounded border shadow-lg"
                      />
                      <p className="text-sm text-gray-500 mt-1">Jelenlegi statisztika kép</p>
                    </div>
                  )}
                  
                  {/* File Upload */}
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleVisitorStatsImageUpload}
                      disabled={visitorStatsImageUploading}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {visitorStatsImageUploading && (
                      <div className="flex items-center text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Feltöltés...
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-2">
                    Töltsön fel egy screenshot-ot a látogatottság statisztikáról (pl. Google Analytics, Matomo). 
                    Ez fog megjelenni a B2B oldalon. Támogatott formátumok: JPEG, PNG, WebP. Maximum fájlméret: 5MB.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Havi Látogatók Száma
                  </label>
                  <input
                    type="number"
                    value={settings.monthly_visitors || ''}
                    onChange={(e) => setSettings(prev => ({...prev, monthly_visitors: parseInt(e.target.value) || null}))}
                    placeholder="52430"
                    className="form-input max-w-xs"
                    disabled={settingsLoading}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    A havi látogatók száma, ami megjelenik a B2B oldalon. Csak számokat adjon meg (pl. 52430).
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={saveSettings}
                    disabled={settingsLoading}
                    className="primary-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {settingsLoading ? 'Mentés...' : 'Beállítások mentése'}
                  </button>
                  
                  {settingsSaved && (
                    <div className="flex items-center text-green-600">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Mentve!
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Legal Pages Management */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Jogi Oldalak Kezelése</h3>
              
              <div className="space-y-6">
                {/* Privacy Policy Editor */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-3">Adatvédelmi Tájékoztató</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cím</label>
                      <input
                        type="text"
                        value={legalPages.privacy.title}
                        onChange={(e) => setLegalPages(prev => ({
                          ...prev,
                          privacy: { ...prev.privacy, title: e.target.value }
                        }))}
                        className="form-input"
                        placeholder="Adatvédelmi és Cookie Tájékoztató"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tartalom</label>
                      <QuillEditor
                        value={legalPages.privacy.content}
                        onChange={(content) => setLegalPages(prev => ({
                          ...prev,
                          privacy: { ...prev.privacy, content }
                        }))}
                        placeholder="Adatvédelmi tájékoztató tartalma..."
                      />
                    </div>
                    <button
                      onClick={() => saveLegalPage('privacy')}
                      disabled={legalPagesLoading}
                      className="primary-button disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {legalPagesLoading ? 'Mentés...' : 'Adatvédelmi tájékoztató mentése'}
                    </button>
                  </div>
                </div>

                <hr className="my-6" />

                {/* Terms of Service Editor */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-3">Általános Szerződési Feltételek</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cím</label>
                      <input
                        type="text"
                        value={legalPages.terms.title}
                        onChange={(e) => setLegalPages(prev => ({
                          ...prev,
                          terms: { ...prev.terms, title: e.target.value }
                        }))}
                        className="form-input"
                        placeholder="Általános Szerződési Feltételek"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tartalom</label>
                      <QuillEditor
                        value={legalPages.terms.content}
                        onChange={(content) => setLegalPages(prev => ({
                          ...prev,
                          terms: { ...prev.terms, content }
                        }))}
                        placeholder="ÁSZF tartalma..."
                      />
                    </div>
                    <button
                      onClick={() => saveLegalPage('terms')}
                      disabled={legalPagesLoading}
                      className="primary-button disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {legalPagesLoading ? 'Mentés...' : 'ÁSZF mentése'}
                    </button>
                  </div>
                </div>

                {legalPagesSaved && (
                  <div className="flex items-center text-green-600">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Jogi oldal mentve!
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-2">Mit követ nyomon a Google Analytics?</h4>
              <div className="text-blue-800 space-y-1">
                <p>• <strong>Oldalmegtekintések:</strong> Minden oldal látogatottsága</p>
                <p>• <strong>Keresések:</strong> Klinika és blog keresések</p>
                <p>• <strong>Kattintások:</strong> Külső linkek (weboldal, social media)</p>
                <p>• <strong>Időpontfoglalás:</strong> Telefon hívások</p>
                <p>• <strong>Felhasználói élmény:</strong> Görgetés, interakciók</p>
              </div>
            </div>
            
            {/* OpenGraph Information */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
              <h4 className="font-semibold text-green-900 mb-2">OpenGraph Meta Tagek működése</h4>
              <div className="text-green-800 space-y-1">
                <p>• <strong>Főoldal:</strong> Alapértelmezett OG kép, site név, főoldal leírás</p>
                <p>• <strong>Blog cikkek:</strong> Cikk fő képe (vagy alapértelmezett), cikk címe, cikk leírása</p>
                <p>• <strong>Klinika profilok:</strong> Klinika profil képe (vagy alapértelmezett), klinika neve, klinika leírása</p>
                <p>• <strong>Egyéb oldalak:</strong> Alapértelmezett OG kép, oldal címe, oldal leírása</p>
                <p>• <strong>Automatikus beállítás:</strong> og:type (website/article), og:url, og:site_name minden oldalon</p>
              </div>
            </div>
          </div>
        )}

        {/* Import View */}
        {adminView === 'import' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Klinika Import</h2>
            
            {/* Step Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className={`flex items-center ${importStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    1
                  </div>
                  <span className="ml-2 font-medium">Fájl feltöltés</span>
                </div>
                <div className={`flex items-center ${importStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    2
                  </div>
                  <span className="ml-2 font-medium">Mező egyeztetés</span>
                </div>
                <div className={`flex items-center ${importStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    3
                  </div>
                  <span className="ml-2 font-medium">Előnézet</span>
                </div>
                <div className={`flex items-center ${importStep >= 4 ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 4 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    4
                  </div>
                  <span className="ml-2 font-medium">Import befejezve</span>
                </div>
              </div>
            </div>

            {/* Step 1: File Upload */}
            {importStep === 1 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Fájl vagy URL megadása</h3>
                
                <div className="space-y-6">
                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fájl feltöltés
                    </label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.xml"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Támogatott formátumok: CSV, Excel (.xlsx, .xls), XML
                    </p>
                  </div>

                  {/* URL Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vagy URL megadása
                    </label>
                    <input
                      type="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://example.com/data.csv vagy Google Sheets link"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Google Sheets, CSV, Excel vagy XML fájl URL-je
                    </p>
                  </div>

                  {/* Help Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Fontos információk:</h4>
                    <div className="text-blue-800 space-y-1 text-sm">
                      <p>• <strong>Budapest kerületek:</strong> A rendszer automatikusan felismeri a kerületeket az irányítószámból</p>
                      <p>• <strong>Város kinyerés:</strong> Ha a cím tartalmazza a várost, automatikusan kinyeri</p>
                      <p>• <strong>Irányítószám logika:</strong> 1076 → 7. kerület (középső 2 szám)</p>
                      <p>• <strong>Duplikátumok:</strong> Név + város alapján észleli a már létező klinikákat</p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={analyzeImportFile}
                      disabled={(!importFile && !importUrl) || importLoading}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {importLoading ? 'Elemzés...' : 'Fájl elemzése'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Field Mapping */}
            {importStep === 2 && importSchema && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">2. Mező egyeztetés</h3>
                
                <div className="space-y-6">
                  {/* File Info */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Fájl sikeresen elemezve:</h4>
                    <div className="text-green-800 space-y-1 text-sm">
                      <p>• <strong>Fájl típus:</strong> {importSchema.file_type}</p>
                      <p>• <strong>Sorok száma:</strong> {importSchema.total_rows}</p>
                      <p>• <strong>Oszlopok száma:</strong> {importSchema.fields?.length || 0}</p>
                    </div>
                  </div>

                  {/* Field Mapping Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Forrás mező
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Minta adat
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cél mező
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Transzformáció
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Műveletek
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {importMappings.map((mapping, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {mapping.source_field}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {importSchema.sample_data && importSchema.sample_data[0] ? 
                                importSchema.sample_data[0][mapping.source_field] || '-' : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={mapping.target_field}
                                onChange={(e) => updateMapping(index, 'target_field', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="">-- Válasszon célmezőt --</option>
                                {clinicFields.map(field => (
                                  <option key={field.field} value={field.field}>
                                    {field.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={mapping.transformation}
                                onChange={(e) => updateMapping(index, 'transformation', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                {transformations.map(transform => (
                                  <option key={transform.key} value={transform.key}>
                                    {transform.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => addAdditionalMapping(mapping.source_field)}
                                  className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-300 rounded hover:bg-green-50"
                                  title="További leképezés hozzáadása ehhez a forrás mezőhöz"
                                >
                                  + Újra
                                </button>
                                {importMappings.filter(m => m.source_field === mapping.source_field).length > 1 && (
                                  <button
                                    onClick={() => removeMapping(index)}
                                    className="text-red-600 hover:text-red-900 text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50"
                                    title="Leképezés eltávolítása"
                                  >
                                    Törlés
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setImportStep(1)}
                      className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Vissza
                    </button>
                    <button
                      onClick={previewImport}
                      disabled={importLoading || !importMappings.some(m => m.target_field)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {importLoading ? 'Előnézet készítése...' : 'Előnézet'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Preview */}
            {importStep === 3 && importPreview && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">3. Import előnézet</h3>
                
                <div className="space-y-6">
                  {/* Preview Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900">Összes sor</h4>
                      <p className="text-2xl font-bold text-blue-700">
                        {importPreview.total_rows || 0}
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900">Érvényes sorok</h4>
                      <p className="text-2xl font-bold text-green-700">
                        {importPreview.valid_rows || 0}
                      </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-900">Hibák</h4>
                      <p className="text-2xl font-bold text-yellow-700">
                        {importPreview.errors?.length || 0}
                      </p>
                    </div>
                  </div>

                  {/* Preview Table */}
                  {importPreview.preview_data && importPreview.preview_data.length > 0 && (
                    <div className="overflow-x-auto">
                      <h4 className="font-semibold text-gray-800 mb-2">Első 5 sor előnézete:</h4>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Név
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Város
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Telefon
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Státusz
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {importPreview.preview_data.slice(0, 5).map((clinic, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {clinic.transformed_data?.name || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {clinic.transformed_data?.city || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {clinic.transformed_data?.phone || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {clinic.transformed_data?.email || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  !clinic.valid ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {!clinic.valid ? 'Hibás' : 'Érvényes'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      onClick={() => setImportStep(2)}
                      className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Vissza
                    </button>
                    <button
                      onClick={executeImport}
                      disabled={importLoading}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {importLoading ? 'Import futtatása...' : 'Import indítása'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Completion */}
            {importStep === 4 && importResult && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Import befejezve</h3>
                
                <div className="space-y-6">
                  {/* Success Message */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-green-900 mb-2">Import sikeresen befejezve!</h4>
                    <p className="text-green-700">A klinikák importálása sikeresen megtörtént.</p>
                  </div>

                  {/* Import Results */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900">Feldolgozott sorok</h4>
                      <p className="text-2xl font-bold text-blue-700">
                        {importResult.processed_rows || 0}
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900">Új klinikák létrehozva</h4>
                      <p className="text-2xl font-bold text-green-700">
                        {importResult.created_clinics || 0}
                      </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-900">Duplikátumok kihagyva</h4>
                      <p className="text-2xl font-bold text-yellow-700">
                        {importResult.duplicates_skipped || 0}
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900">Hibák</h4>
                      <p className="text-2xl font-bold text-red-700">
                        {importResult.errors || 0}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={() => {
                        setImportStep(1);
                        setImportFile(null);
                        setImportUrl('');
                        setImportSchema(null);
                        setImportMappings([]);
                        setImportPreview(null);
                        setImportResult(null);
                      }}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Új import indítása
                    </button>
                    <button
                      onClick={() => setAdminView('clinics')}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Klinikák megtekintése
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {adminView === 'messages' && (
          <AdminMessages />
        )}
      </div>
    </div>
  );
};

// CRM Component


const CRMView = ({ adminToken, backendUrl }) => {
  const [crmClinics, setCrmClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [showClinicDetails, setShowClinicDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [countyFilter, setCountyFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [subscriberStatusFilter, setSubscriberStatusFilter] = useState('');
  const [billingStatusFilter, setBillingStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [crmStats, setCrmStats] = useState(null);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [expiringFilter, setExpiringFilter] = useState(false); // New filter for expiring packages

  // Load CRM data
  const loadCrmData = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    try {
      // Use regular clinics endpoint with CRM defaults since CRM endpoints might not be deployed
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (cityFilter) params.append('city', cityFilter);
      if (countyFilter) params.append('county', countyFilter);
      params.append('limit', '100');

      const response = await fetch(`${backendUrl}/api/admin/clinics?${params}`, {
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Add CRM defaults for clinics that don't have them
        const clinicsWithCrm = data.map(clinic => ({
          ...clinic,
          package_type: clinic.package_type || "Ingyenes",
          subscriber_status: clinic.subscriber_status || "Nincs", 
          billing_status: clinic.billing_status || "Nincs",
          internal_notes: clinic.internal_notes || "",
          package_expiry_date: clinic.package_expiry_date || null
        }));
        
        // Apply client-side filtering for CRM fields
        let filteredClinics = clinicsWithCrm;
        
        if (packageFilter) {
          filteredClinics = filteredClinics.filter(clinic => clinic.package_type === packageFilter);
        }
        if (subscriberStatusFilter) {
          filteredClinics = filteredClinics.filter(clinic => clinic.subscriber_status === subscriberStatusFilter);
        }
        if (billingStatusFilter) {
          filteredClinics = filteredClinics.filter(clinic => clinic.billing_status === billingStatusFilter);
        }
        
        // Apply expiring filter
        if (expiringFilter) {
          const today = new Date();
          const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
          
          filteredClinics = filteredClinics.filter(clinic => {
            // Only check paid packages (Alap or Prémium)
            if (clinic.package_type === 'Ingyenes' || !clinic.package_expiry_date) {
              return false;
            }
            
            const expiryDate = new Date(clinic.package_expiry_date);
            // Include if already expired or expires within 30 days
            return expiryDate <= thirtyDaysFromNow;
          });
        }
        
        // Apply client-side sorting
        filteredClinics.sort((a, b) => {
          let aVal = a[sortBy] || '';
          let bVal = b[sortBy] || '';
          
          if (sortBy === 'package_expiry_date') {
            aVal = aVal || '9999-12-31'; // Put null dates at end
            bVal = bVal || '9999-12-31';
          }
          
          if (sortOrder === 'asc') {
            return aVal.toString().localeCompare(bVal.toString());
          } else {
            return bVal.toString().localeCompare(aVal.toString());
          }
        });
        
        setCrmClinics(filteredClinics);
      } else {
        console.error('Failed to load CRM clinics', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Response text:', errorText.substring(0, 200));
      }
    } catch (error) {
      console.error('Error loading CRM data:', error);
    }
    setLoading(false);
  };

  // Load CRM statistics
  const loadCrmStats = async () => {
    if (!adminToken) return;
    
    try {
      // Use regular clinics endpoint to calculate stats client-side
      const response = await fetch(`${backendUrl}/api/admin/clinics?limit=200`, {
        headers: { 
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Calculate stats client-side
        const clinicsWithCrm = data.map(clinic => ({
          ...clinic,
          package_type: clinic.package_type || "Ingyenes",
          subscriber_status: clinic.subscriber_status || "Nincs",
          billing_status: clinic.billing_status || "Nincs"
        }));
        
        const packageStats = {};
        const subscriberStats = {};
        const billingStats = {};
        
        clinicsWithCrm.forEach(clinic => {
          packageStats[clinic.package_type] = (packageStats[clinic.package_type] || 0) + 1;
          subscriberStats[clinic.subscriber_status] = (subscriberStats[clinic.subscriber_status] || 0) + 1;
          billingStats[clinic.billing_status] = (billingStats[clinic.billing_status] || 0) + 1;
        });
        
        // Calculate expiring soon (next 30 days)
        const today = new Date();
        const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
        const expiringSoon = clinicsWithCrm.filter(clinic => {
          if (!clinic.package_expiry_date || clinic.package_type === 'Ingyenes') return false;
          const expiryDate = new Date(clinic.package_expiry_date);
          return expiryDate <= thirtyDaysFromNow;
        }).length;
        
        setCrmStats({
          total_clinics: clinicsWithCrm.length,
          package_stats: packageStats,
          subscriber_stats: subscriberStats,
          billing_stats: billingStats,
          expiring_soon: expiringSoon
        });
      } else {
        console.error('Failed to load CRM stats', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Response text:', errorText.substring(0, 200));
      }
    } catch (error) {
      console.error('Error loading CRM stats:', error);
    }
  };

  // Load counties and cities for filters
  const loadFiltersData = async () => {
    try {
      // Load counties
      const countiesResponse = await fetch(`${backendUrl}/counties`);
      if (countiesResponse.ok) {
        const countiesData = await countiesResponse.json();
        setCounties(countiesData);
      }

      // Load cities
      const citiesResponse = await fetch(`${backendUrl}/cities?limit=200`);
      if (citiesResponse.ok) {
        const citiesData = await citiesResponse.json();
        setCities(citiesData);
      }
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  };

  // Load clinic details for CRM - use client-side data since endpoint might not exist
  const loadClinicDetails = (clinicId) => {
    // Find clinic in already loaded data
    const clinic = crmClinics.find(c => c.id === clinicId);
    if (clinic) {
      // Add CRM defaults if missing
      const clinicWithCrm = {
        ...clinic,
        package_type: clinic.package_type || "Ingyenes",
        subscriber_status: clinic.subscriber_status || "Nincs",
        billing_status: clinic.billing_status || "Nincs", 
        internal_notes: clinic.internal_notes || "",
        package_expiry_date: clinic.package_expiry_date || null
      };
      setSelectedClinic(clinicWithCrm);
      setShowClinicDetails(true);
    } else {
      console.error('Clinic not found in loaded data');
      alert('Klinika adatok nem találhatók');
    }
  };

  // Update CRM fields - use client-side update since endpoints might not be available
  const updateCrmFields = async (clinicId, updateData) => {
    if (!adminToken) return;
    
    try {
      // Try CRM endpoint first
      try {
        const response = await fetch(`${backendUrl}/api/admin/crm/clinics/${clinicId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          // Update worked, reload data
          await loadCrmData();
          loadClinicDetails(clinicId);
          return true;
        }
      } catch (crmError) {
        console.log('CRM endpoint not available, trying regular clinic update...');
      }

      // Try regular clinic update
      try {
        const response = await fetch(`${backendUrl}/api/admin/clinics/${clinicId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          // Update worked, reload data
          await loadCrmData();
          loadClinicDetails(clinicId);
          return true;
        }
      } catch (regularError) {
        console.log('Regular clinic endpoint also not available...');
      }

      // If all endpoints fail, update client-side temporarily
      console.log('All backend endpoints failed, updating client-side...');
      
      // Update the local clinic data
      const updatedClinics = crmClinics.map(clinic => {
        if (clinic.id === clinicId) {
          return { ...clinic, ...updateData };
        }
        return clinic;
      });
      setCrmClinics(updatedClinics);
      
      // Update selected clinic
      if (selectedClinic && selectedClinic.id === clinicId) {
        setSelectedClinic({ ...selectedClinic, ...updateData });
      }
      
      alert('Adatok ideiglenesen helyben mentve. Teljes mentéshez backend frissítés szükséges.');
      return true;

    } catch (error) {
      console.error('Error updating CRM fields:', error);
      alert('Hiba történt a frissítés során');
      return false;
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadCrmData();
  }, [adminToken, searchTerm, cityFilter, countyFilter, packageFilter, subscriberStatusFilter, billingStatusFilter, sortBy, sortOrder, expiringFilter]);

  useEffect(() => {
    loadCrmStats();
    loadFiltersData();
  }, [adminToken]);

  // Helper function to format social links
  const formatSocialLinks = (clinic) => {
    const links = [];
    
    if (clinic.website) {
      links.push({ type: 'Weboldal', url: clinic.website, icon: '🌐' });
    }
    
    if (clinic.subpage_url) {
      links.push({ type: 'FogPortál Aloldal', url: clinic.subpage_url, icon: '📄' });
    }
    
    if (clinic.facebook_url) {
      links.push({ type: 'Facebook', url: clinic.facebook_url, icon: '📘' });
    }
    
    if (clinic.instagram_url) {
      links.push({ type: 'Instagram', url: clinic.instagram_url, icon: '📷' });
    }
    
    if (clinic.linkedin_url) {
      links.push({ type: 'LinkedIn', url: clinic.linkedin_url, icon: '💼' });
    }
    
    if (clinic.youtube_url) {
      links.push({ type: 'YouTube', url: clinic.youtube_url, icon: '📺' });
    }
    
    return links;
  };

  // Get package color class
  const getPackageColorClass = (packageType) => {
    switch (packageType) {
      case 'Ingyenes': return 'bg-gray-100 text-gray-800';
      case 'Alap': return 'bg-blue-100 text-blue-800';
      case 'Prémium': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get subscriber status color class
  const getSubscriberStatusColorClass = (status) => {
    switch (status) {
      case 'Nincs': return 'bg-gray-100 text-gray-800';
      case 'Felkeresve': return 'bg-yellow-100 text-yellow-800';
      case 'Érdekli': return 'bg-blue-100 text-blue-800';
      case 'Előfizető': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get billing status color class
  const getBillingStatusColorClass = (status) => {
    switch (status) {
      case 'Nincs': return 'bg-gray-100 text-gray-800';
      case 'Számlázva': return 'bg-orange-100 text-orange-800';
      case 'Fizetve': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800">CRM - Ügyfélkezelés</h2>
      </div>

      {/* CRM Statistics */}
      {crmStats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800">Összes klinika</h3>
            <p className="text-3xl font-bold text-blue-600">{crmStats.total_clinics}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800">Ingyenes</h3>
            <p className="text-3xl font-bold text-gray-600">{crmStats.package_stats['Ingyenes'] || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800">Alap</h3>
            <p className="text-3xl font-bold text-blue-600">{crmStats.package_stats['Alap'] || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800">Prémium</h3>
            <p className="text-3xl font-bold text-purple-600">{crmStats.package_stats['Prémium'] || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-800">Hamarosan lejár</h3>
            <p className="text-3xl font-bold text-red-600">{crmStats.expiring_soon}</p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Szűrők és keresés</h3>
        
        {/* Special Filters Row */}
        <div className="mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={expiringFilter}
              onChange={(e) => setExpiringFilter(e.target.checked)}
              className="form-checkbox h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <span className="text-sm font-medium text-gray-700">
              🔴 Hamarosan lejárók (30 napon belül vagy már lejárt)
            </span>
          </label>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Keresés (klinika név)</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Klinika neve..."
              className="form-input"
            />
          </div>

          {/* City Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Város</label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Összes város</option>
              {cities.map(city => (
                <option key={city.id} value={city.name}>{city.name}</option>
              ))}
            </select>
          </div>

          {/* County Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vármegye</label>
            <select
              value={countyFilter}
              onChange={(e) => setCountyFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Összes vármegye</option>
              {counties.map(county => (
                <option key={county.id} value={county.name}>{county.name}</option>
              ))}
            </select>
          </div>

          {/* Package Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Csomag</label>
            <select
              value={packageFilter}
              onChange={(e) => setPackageFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Összes csomag</option>
              <option value="Ingyenes">Ingyenes</option>
              <option value="Alap">Alap</option>
              <option value="Prémium">Prémium</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Subscriber Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Előfizetői állapot</label>
            <select
              value={subscriberStatusFilter}
              onChange={(e) => setSubscriberStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Összes állapot</option>
              <option value="Nincs">Nincs</option>
              <option value="Felkeresve">Felkeresve</option>
              <option value="Érdekli">Érdekli</option>
              <option value="Előfizető">Előfizető</option>
            </select>
          </div>

          {/* Billing Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Számlázási állapot</label>
            <select
              value={billingStatusFilter}
              onChange={(e) => setBillingStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Összes állapot</option>
              <option value="Nincs">Nincs</option>
              <option value="Számlázva">Számlázva</option>
              <option value="Fizetve">Fizetve</option>
            </select>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rendezés</label>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-input flex-1"
              >
                <option value="name">Név</option>
                <option value="city">Város</option>
                <option value="package_type">Csomag</option>
                <option value="subscriber_status">Előfizetői állapot</option>
                <option value="billing_status">Számlázási állapot</option>
                <option value="package_expiry_date">Lejárati dátum</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Clinics Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">Klinikák ({crmClinics.length})</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Betöltés...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Klinika</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Social/Web</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Csomag</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Előfizetői állapot</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Számlázási állapot</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lejárati dátum</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {crmClinics.map((clinic) => (
                  <tr key={clinic.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <button
                          onClick={() => loadClinicDetails(clinic.id)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {clinic.name}
                        </button>
                        <p className="text-sm text-gray-500">{clinic.city}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        {formatSocialLinks(clinic).map((link, index) => (
                          <a
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={link.type}
                            className="text-lg hover:scale-110 transition-transform"
                          >
                            {link.icon}
                          </a>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${getPackageColorClass(clinic.package_type)}`}>
                        {clinic.package_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${getSubscriberStatusColorClass(clinic.subscriber_status)}`}>
                        {clinic.subscriber_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${getBillingStatusColorClass(clinic.billing_status)}`}>
                        {clinic.billing_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {clinic.package_expiry_date || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Clinic Details Modal */}
      {showClinicDetails && selectedClinic && (
        <ClinicDetailsModal 
          clinic={selectedClinic}
          onClose={() => {
            setShowClinicDetails(false);
            setSelectedClinic(null);
          }}
          onUpdate={updateCrmFields}
        />
      )}
    </div>
  );
};

// Clinic Details Modal Component


const ClinicDetailsModal = ({ clinic, onClose, onUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    package_type: clinic.package_type,
    subscriber_status: clinic.subscriber_status,
    billing_status: clinic.billing_status,
    internal_notes: clinic.internal_notes,
    package_expiry_date: clinic.package_expiry_date || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const success = await onUpdate(clinic.id, editData);
    if (success) {
      setEditMode(false);
    }
    setSaving(false);
  };

  const formatSocialLinks = (clinic) => {
    const links = [];
    
    if (clinic.website) {
      links.push({ type: 'Weboldal', url: clinic.website, icon: '🌐' });
    }
    
    if (clinic.subpage_url) {
      links.push({ type: 'FogPortál Aloldal', url: clinic.subpage_url, icon: '📄' });
    }
    
    if (clinic.facebook_url) {
      links.push({ type: 'Facebook', url: clinic.facebook_url, icon: '📘' });
    }
    
    if (clinic.instagram_url) {
      links.push({ type: 'Instagram', url: clinic.instagram_url, icon: '📷' });
    }
    
    if (clinic.linkedin_url) {
      links.push({ type: 'LinkedIn', url: clinic.linkedin_url, icon: '💼' });
    }
    
    if (clinic.youtube_url) {
      links.push({ type: 'YouTube', url: clinic.youtube_url, icon: '📺' });
    }
    
    return links;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-8 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">CRM - {clinic.name}</h2>
          <div className="flex space-x-2">
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Szerkesztés
              </button>
            )}
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Clinic Info */}
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Klinika adatok</h3>
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">Név:</span>
                  <span className="ml-2">{clinic.name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Cím:</span>
                  <span className="ml-2">{clinic.address}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Város:</span>
                  <span className="ml-2">{clinic.city}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Telefon:</span>
                  <span className="ml-2">{clinic.phone}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <span className="ml-2">{clinic.email}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Social és Web profilok</h3>
              <div className="space-y-2">
                {formatSocialLinks(clinic).map((link, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-lg">{link.icon}</span>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {link.type}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatások</h3>
              <div className="flex flex-wrap gap-2">
                {clinic.services.map((service, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Leírás</h3>
              <p className="text-gray-700">{clinic.description}</p>
            </div>
          </div>

          {/* Right Column - CRM Data */}
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">CRM beállítások</h3>
              
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Csomag típus</label>
                    <select
                      value={editData.package_type}
                      onChange={(e) => setEditData({...editData, package_type: e.target.value})}
                      className="form-input"
                    >
                      <option value="Ingyenes">Ingyenes</option>
                      <option value="Alap">Alap</option>
                      <option value="Prémium">Prémium</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Előfizetői állapot</label>
                    <select
                      value={editData.subscriber_status}
                      onChange={(e) => setEditData({...editData, subscriber_status: e.target.value})}
                      className="form-input"
                    >
                      <option value="Nincs">Nincs</option>
                      <option value="Felkeresve">Felkeresve</option>
                      <option value="Érdekli">Érdekli</option>
                      <option value="Előfizető">Előfizető</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Számlázási állapot</label>
                    <select
                      value={editData.billing_status}
                      onChange={(e) => setEditData({...editData, billing_status: e.target.value})}
                      className="form-input"
                    >
                      <option value="Nincs">Nincs</option>
                      <option value="Számlázva">Számlázva</option>
                      <option value="Fizetve">Fizetve</option>
                    </select>
                  </div>

                  {(editData.package_type === 'Alap' || editData.package_type === 'Prémium') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Csomag lejárati dátum</label>
                      <input
                        type="date"
                        value={editData.package_expiry_date}
                        onChange={(e) => setEditData({...editData, package_expiry_date: e.target.value})}
                        className="form-input"
                      />
                    </div>
                  )}

                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Mentés...' : 'Mentés'}
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setEditData({
                          package_type: clinic.package_type,
                          subscriber_status: clinic.subscriber_status,
                          billing_status: clinic.billing_status,
                          internal_notes: clinic.internal_notes,
                          package_expiry_date: clinic.package_expiry_date || ''
                        });
                      }}
                      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
                    >
                      Mégse
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">Csomag:</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                      clinic.package_type === 'Ingyenes' ? 'bg-gray-100 text-gray-800' :
                      clinic.package_type === 'Alap' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {clinic.package_type}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Előfizetői állapot:</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                      clinic.subscriber_status === 'Nincs' ? 'bg-gray-100 text-gray-800' :
                      clinic.subscriber_status === 'Felkeresve' ? 'bg-yellow-100 text-yellow-800' :
                      clinic.subscriber_status === 'Érdekli' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {clinic.subscriber_status}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Számlázási állapot:</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                      clinic.billing_status === 'Nincs' ? 'bg-gray-100 text-gray-800' :
                      clinic.billing_status === 'Számlázva' ? 'bg-orange-100 text-orange-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {clinic.billing_status}
                    </span>
                  </div>
                  {clinic.package_expiry_date && (
                    <div>
                      <span className="font-medium text-gray-700">Lejárati dátum:</span>
                      <span className="ml-2">{clinic.package_expiry_date}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-yellow-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Belső jegyzetek</h3>
              
              {editMode ? (
                <textarea
                  value={editData.internal_notes}
                  onChange={(e) => setEditData({...editData, internal_notes: e.target.value})}
                  placeholder="Belső jegyzetek..."
                  className="form-input h-32"
                />
              ) : (
                <div className="text-gray-700 whitespace-pre-wrap">
                  {clinic.internal_notes || 'Nincs jegyzet'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};





export default AdminPanel;
